import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, Suspense, forwardRef, lazy, useLayoutEffect, isValidElement, Fragment as Fragment$1 } from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import classNames from 'classnames';
import { useHistory, useRouteBlocker, isLegacyContextInformation, OldToNewContext, useActiveContextInformation, useCustomContext, useLocation, matchPath, Link, RouteContext, PlatformLink } from '@interhyp/platform-router';
import { useMeasure, useMedia } from 'react-use';
import { generateToast, Button, Notification, IconButton, Modal, ModalContent, ModalFooter, Typography, Separator, Skeleton, Tab, TabItem, Breadcrumbs, Link as Link$1, MaybeWrapper, Tooltip, InfoTag, GroupTitle, ResetButton, IconRenderer, ClickableDiv, ContextMenu, useMergedRef } from '@platform/elements';
import { useTransition, animated } from '@react-spring/web';
import { TOOL_AREA_ELEMENT_ORDER, toolAreaPlugin, localToolAreaPlugin } from '@interhyp/api-toolarea';
import { toolAreaDrawerValue, portalNamespace } from '@interhyp/api-values';
import { createStorageValue } from '@interhyp/platform-storage';
import { z } from 'zod';
import { ArrowForwardIos, ArrowBackwards, CallFilled, ArrowForward, Menu, ChevronDown, Close, MoreVert } from '@interhyp/platform-icons';
import { ErrorBoundary } from 'react-error-boundary';
import { landingPageUrlPlugin } from '@interhyp/api-portal';
import AnimateHeight from 'react-animate-height';
import debounce from 'lodash.debounce';
import SwiperCore from 'swiper';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useMergeRefs, FloatingPortal, FloatingFocusManager, useFloating, autoUpdate, offset, shift, useDismiss, useRole, useHover, safePolygon, useInteractions } from '@floating-ui/react';
import { Logo } from '@platform/theme';

const footerContext = /*#__PURE__*/createContext(undefined);

// This context only exists because the other footer context had a ReactComponent as type
const footerRenderingContext = /*#__PURE__*/createContext({
  footerIsRendered: false,
  setFooterIsRendered: () => void 0
});

function Footer() {
  const footer = useContext(footerContext);
  const {
    setFooterIsRendered
  } = useContext(footerRenderingContext);
  useEffect(() => {
    if (footer) {
      setFooterIsRendered(true);
    }
    return () => setFooterIsRendered(false);
  }, [footer, setFooterIsRendered]);
  if (!footer) {
    return null;
  }
  return footer;
}

/*
This Hook returns the footer component provided by the FooterContext, but only if it not has been rendered yet
 */
const useFallbackFooter = () => {
  const {
    footerIsRendered
  } = useContext(footerRenderingContext);
  const footer = useContext(footerContext);
  const [shouldShowFallbackFooter, setShouldShowFallbackFooter] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      setShouldShowFallbackFooter(!footerIsRendered);
    }, 1);
  }, [footerIsRendered]);
  return useMemo(() => {
    if (shouldShowFallbackFooter) {
      return footer ?? null;
    }
    return null;
  }, [footer, shouldShowFallbackFooter]);
};

function FooterContext({
  children,
  footer
}) {
  const [footerIsRendered, setFooterIsRendered] = useState(false);
  const footerRenderingValue = useMemo(() => ({
    footerIsRendered,
    setFooterIsRendered
  }), [footerIsRendered]);
  return /*#__PURE__*/jsx(footerContext.Provider, {
    value: footer,
    children: /*#__PURE__*/jsx(footerRenderingContext.Provider, {
      value: footerRenderingValue,
      children: children
    })
  });
}

var styles$M = {"rootLayout":"rootLayout_-J-oMsxSwr"};

function RootLayout({
  className,
  ...other
}) {
  return /*#__PURE__*/jsx("div", {
    className: classNames(className, styles$M.rootLayout),
    ...other
  });
}

const notImplementedFunc = () => console.error('BotomBarContext not available because BottomBarProvider is not in use.');
const BottomBarContext = /*#__PURE__*/createContext({
  controllers: [],
  addBottomBarController: notImplementedFunc,
  removeBottomBarController: notImplementedFunc,
  setIgnoreRouteBlockerOnLastPathLevel: notImplementedFunc,
  onSubmit: notImplementedFunc,
  onDiscard: notImplementedFunc,
  isDirty: false,
  isDisabled: false,
  isLoading: false
});

/**
 * BottomBarProvider manages registered bottom bar controllers
 */
function BottomBarProvider({
  children
}) {
  const [controllers, setControllers] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [ignoreRouteBlockerOnLastPathLevel, setIgnoreRouteBlockerOnLastPathLevel] = useState(false);
  const history = useHistory();
  const addBottomBarController = useCallback(controller => {
    setControllers(previousValue => [...previousValue.filter(v => v !== controller), controller]);
  }, []);
  const removeBottomBarController = useCallback(controller => {
    setControllers(previousValue => [...previousValue.filter(v => v !== controller)]);
  }, []);
  const isDirty = useMemo(() => {
    return controllers.some(controller => controller.isDirty);
  }, [controllers]);
  const isDisabled = useMemo(() => {
    return controllers.some(controller => controller.isDisabled);
  }, [controllers]);
  const onSubmit = useCallback(async () => {
    setLoading(true);
    const response = await Promise.all(controllers.filter(controller => controller.isDirty && !!controller.submitHandler).map(async controller => {
      return controller.submitHandler();
    }));
    if (response.includes(false)) {
      generateToast('error', 'Fehler', 'Es ist ein Fehler bei dem Speichern der Seite aufgetreten.');
    } else {
      generateToast('success', 'Erfolg', 'Die Seite wurde erfolgreich gespeichert.');
    }
    setLoading(false);
  }, [controllers]);
  const onDiscard = useCallback(async () => {
    setLoading(true);
    await Promise.all(controllers.filter(controller => controller.isDirty).map(async controller => {
      await controller.discardHandler?.();
    }));
    setLoading(false);
  }, [controllers]);
  useRouteBlocker(isDirty, {
    onSave: onSubmit,
    onProceed: onDiscard,
    pathDefinition: ignoreRouteBlockerOnLastPathLevel ? {
      path: history?.location.pathname.split('/').slice(0, -1).join('/'),
      exact: false
    } : undefined
  }, [onSubmit, onDiscard, ignoreRouteBlockerOnLastPathLevel]);
  const values = useMemo(() => {
    return {
      controllers,
      addBottomBarController,
      removeBottomBarController,
      setIgnoreRouteBlockerOnLastPathLevel,
      isDirty,
      isDisabled,
      isLoading,
      onSubmit,
      onDiscard
    };
  }, [controllers, addBottomBarController, removeBottomBarController, setIgnoreRouteBlockerOnLastPathLevel, isDirty, isDisabled, isLoading, onSubmit, onDiscard]);
  return /*#__PURE__*/jsx(BottomBarContext.Provider, {
    value: values,
    children: children
  });
}

var styles$L = {"bottomBar":"bottomBar_N89rC9fSC5"};

function BottomBar(props) {
  const {
    isDirty,
    isDisabled,
    isLoading,
    onSubmit,
    onDiscard
  } = useContext(BottomBarContext);
  const transition = useTransition(isDirty, {
    from: {
      transform: 'translateY(100%)'
    },
    enter: {
      transform: 'translateY(0%)'
    },
    leave: {
      transform: 'translateY(100%)'
    }
  });
  return transition((transitionStyles, item) => item && /*#__PURE__*/jsxs(animated.div, {
    className: classNames(styles$L.bottomBar, props.className),
    style: transitionStyles,
    children: [/*#__PURE__*/jsx(Button, {
      type: "submit",
      onClick: onSubmit,
      disabled: isDisabled || !isDirty,
      "data-testid": "main-save-button",
      loading: isLoading,
      children: "Speichern"
    }), /*#__PURE__*/jsx(Button, {
      type: "reset",
      variant: "outlined",
      onClick: onDiscard,
      disabled: isDisabled || !isDirty || isLoading,
      "data-testid": "main-reset-button",
      children: "\xC4nderungen verwerfen"
    })]
  }));
}

function Content({
  element,
  componentArgs
}) {
  if (!element) {
    return null;
  }
  return /*#__PURE__*/jsx(Suspense, {
    fallback: /*#__PURE__*/jsx("div", {
      children: "Loading"
    }),
    children: /*#__PURE__*/jsx(ErrorBoundary, {
      fallback: /*#__PURE__*/jsx(Notification, {
        type: "error",
        children: "Es ist ein Fehler beim Laden der Komponente aufgetreten."
      }),
      children: /*#__PURE__*/jsx(element.component, {
        ...componentArgs
      })
    }, element.slug)
  });
}

var styles$K = {"toolArea":"toolArea_BFo0a-crBT","iconBar":"iconBar_3C5rKi11CU","iconButton":"iconButton_XEB6zNGS01","active":"active_ru6hCGCJN-","disabled":"disabled_VIEcFX2ILh","iconBarSeparator":"iconBarSeparator_yOfmiH0jj-","iconBarSegment":"iconBarSegment_7IktKFi6QI","toolAreaInnerContent":"toolAreaInnerContent_sYfGYBs0XJ","toolAreaContent":"toolAreaContent_6E1U4f07H9","hidden":"hidden_FqzTIxf-PL","aboveContent":"aboveContent_J7m1klyOLA","contentHeader":"contentHeader_trScPtbXvi"};

function ToolAreaIconButton({
  element,
  currentDrawer,
  handleIconClick
}) {
  return /*#__PURE__*/jsx(IconButton, {
    title: element.title,
    onClick: () => !element.disabled && handleIconClick(element),
    className: classNames(styles$K.iconButton, {
      [styles$K.active]: element.slug === currentDrawer?.slug,
      [styles$K.disabled]: element.disabled
    }),
    tertiary: true,
    disabled: element.disabled,
    children: element.icon
  });
}

const useActiveContent = ({
  elements,
  context,
  localElements
}) => {
  const [currentModalSlug, setCurrentModalSlug] = useState();
  const [currentDrawerSlug, setCurrentDrawerSlug] = toolAreaDrawerValue.useValue();
  const {
    push
  } = useHistory();
  const currentDrawer = useMemo(() => {
    return [...elements, ...localElements].find(element => element.slug === currentDrawerSlug && element.type === 'COMPONENT' && (element.presentation ?? 'DRAWER') === 'DRAWER');
  }, [elements, localElements, currentDrawerSlug]);
  const currentModal = useMemo(() => {
    return [...elements, ...localElements].find(element => element.slug === currentModalSlug && element.type === 'COMPONENT' && (element.presentation ?? 'DRAWER') === 'MODAL');
  }, [elements, localElements, currentModalSlug]);
  const closeCurrentPresentation = useCallback(() => {
    if (currentModalSlug) {
      setCurrentModalSlug(undefined);
    } else {
      // Because @interhyp/platform-value currently has issues with undefined as a value, this is a workaround
      setCurrentDrawerSlug('');
    }
  }, [currentModalSlug, setCurrentDrawerSlug]);
  const handleIconClick = useCallback(element => {
    if (element.type === 'CLICK') {
      element.callback();
    } else if (element.type === 'LINK' && context) {
      const href = element.href(isLegacyContextInformation(context) ? {
        activeContext: OldToNewContext.get(context.type),
        activeContextLink: context.prefix,
        contextParameter: {
          id: context.id
        },
        resourceId: context.id,
        resourceType: context.type
      } : {
        activeContext: context.context,
        contextParameter: context.parameter,
        activeContextLink: `${context.context.legacySlug}/${context.parameter.id}`,
        // legacy stuff
        resourceId: context.parameter.id,
        // legacy stuff
        resourceType: context.context.legacySlug // legacy stuff
      });
      push(href);
    } else if (element.type === 'COMPONENT') {
      const presentation = element.presentation ?? 'DRAWER';
      if (element.type === 'COMPONENT' && presentation === 'DRAWER') {
        // Because @interhyp/platform-value currently has issues with undefined as a value, this is a workaround
        setCurrentDrawerSlug(currentDrawerSlug === element.slug ? '' : element.slug);
      }
      if (element.type === 'COMPONENT' && presentation === 'MODAL') {
        setCurrentModalSlug(element.slug);
      }
    }
  }, [context, push, setCurrentDrawerSlug, currentDrawerSlug]);
  return {
    handleIconClick,
    currentDrawer,
    currentModal,
    closeCurrentPresentation
  };
};

const useAnimationDisplay = value => {
  const [visualValue, setVisualValue] = useState();
  useEffect(() => {
    if (value) {
      setVisualValue(value);
    }
  }, [value]);
  const animationEnd = useCallback(() => {
    if (!value) {
      setVisualValue(undefined);
    }
  }, [value]);
  return {
    value: visualValue,
    animationEnd
  };
};

function ToolArea({
  elements,
  localElements,
  context,
  drawerAboveContent = true // in the future this should be false
}) {
  const {
    handleIconClick,
    currentDrawer: currentDraw,
    currentModal,
    closeCurrentPresentation
  } = useActiveContent({
    elements,
    localElements,
    context
  });
  const {
    value: currentDrawer,
    animationEnd
  } = useAnimationDisplay(currentDraw);
  const componentArgs = useMemo(() => ({
    visible: true,
    context,
    closePresentation: closeCurrentPresentation
  }), [context, closeCurrentPresentation]);
  if (elements.length === 0 && localElements.length === 0) {
    return null;
  }
  return /*#__PURE__*/jsxs("div", {
    className: styles$K.toolArea,
    children: [/*#__PURE__*/jsxs(Modal, {
      id: "toolarea-modal",
      open: !!currentModal,
      title: currentModal?.title,
      closeable: true,
      closeOnEsc: true,
      closeOnOverlayClick: true,
      close: closeCurrentPresentation,
      size: currentModal?.modelSize,
      children: [/*#__PURE__*/jsx(ModalContent, {
        children: currentModal && /*#__PURE__*/jsx(Content, {
          element: currentModal,
          componentArgs: componentArgs
        })
      }), /*#__PURE__*/jsx(ModalFooter, {})]
    }), /*#__PURE__*/jsx("div", {
      className: classNames(styles$K.toolAreaContent, {
        [styles$K.hidden]: !currentDraw,
        [styles$K.aboveContent]: drawerAboveContent
      }),
      onTransitionEnd: () => animationEnd(),
      children: currentDrawer && /*#__PURE__*/jsxs("div", {
        className: styles$K.toolAreaInnerContent,
        children: [/*#__PURE__*/jsxs("div", {
          className: styles$K.contentHeader,
          children: [/*#__PURE__*/jsx(Typography, {
            variant: "h3",
            children: currentDrawer.title
          }), /*#__PURE__*/jsx(IconButton, {
            onClick: closeCurrentPresentation,
            tertiary: true,
            toolbar: true,
            children: /*#__PURE__*/jsx(ArrowForwardIos, {})
          })]
        }), /*#__PURE__*/jsx(Content, {
          element: currentDrawer,
          componentArgs: componentArgs
        })]
      })
    }), (elements.length > 0 || localElements.length > 0) && /*#__PURE__*/jsxs("div", {
      className: styles$K.iconBar,
      children: [/*#__PURE__*/jsx("div", {
        className: styles$K.iconBarSegment,
        children: elements.map(element => /*#__PURE__*/jsx(ToolAreaIconButton, {
          element: element,
          handleIconClick: handleIconClick,
          currentDrawer: currentDrawer
        }, element.slug))
      }), elements.length > 0 && localElements.length > 0 && /*#__PURE__*/jsx(Separator, {
        variant: "HORIZONTAL",
        className: styles$K.iconBarSeparator
      }), /*#__PURE__*/jsx("div", {
        className: styles$K.iconBarSegment,
        children: localElements.map(element => /*#__PURE__*/jsx(ToolAreaIconButton, {
          element: element,
          handleIconClick: handleIconClick,
          currentDrawer: currentDrawer
        }, element.slug))
      })]
    })]
  });
}

const toolAreaContext = /*#__PURE__*/createContext({
  toolAreaElements: [],
  registerToolAreaElement: () => void 0,
  unregisterToolAreaElement: () => void 0,
  activateLocalToolAreaPlugin: () => void 0,
  deactivateLocalToolAreaPlugin: () => void 0
});

/**
 * Check if the given tool-area element is valid to show inside the given context
 * @param element
 * @param context
 * @param customContext
 */
function checkToolAreaElementIsValidForContext(element, context, customContext) {
  return !!(
  // Element is valid if there is no context and no custom context and the element doesn't require one

  context === undefined && customContext === undefined && (element.contexts?.length ?? 0) === 0 ||
  // It is also valid if there is a context and the element contains it
  context?.context && element.contexts.includes(context?.context) ||
  // same here but legacy check for deprecated "string" context
  context?.context.legacySlug && element.contexts.includes(context?.context.legacySlug) ||
  // same here but legacy check for deprecated custom context
  customContext?.context && element.contexts.includes(customContext.context));
}

/**
 * Sort elements depending on the TOOL_AREA_ELEMENT_ORDER array
 * @param elements
 */
function sortToolAreaElements(elements) {
  return elements.sort((elementA, elementB) => {
    const indexA = TOOL_AREA_ELEMENT_ORDER.indexOf(elementA.slug);
    const indexB = TOOL_AREA_ELEMENT_ORDER.indexOf(elementB.slug);
    if (indexA === indexB) {
      return 0;
    }
    if (indexA === -1) {
      return 1;
    }
    if (indexB === -1) {
      return -1;
    }
    return indexA - indexB;
  });
}

const toolAreaAboveContent = createStorageValue({
  id: Symbol('tool-area-above-content'),
  namespace: portalNamespace,
  schema: z.boolean(),
  type: 'local',
  initialValue: true
});
function ToolAreaConnected() {
  const elements = toolAreaPlugin.useExtensions();
  const context = useActiveContextInformation();
  const customContext = useCustomContext();
  const {
    toolAreaElements: localElements
  } = useContext(toolAreaContext);
  const [drawerAboveContent] = toolAreaAboveContent.useValue();
  const sortedElements = useMemo(() => {
    const filteredElements = elements.filter(element => checkToolAreaElementIsValidForContext(element, context, customContext));
    return sortToolAreaElements(filteredElements);
  }, [context, customContext, elements]);
  const filteredLocalElements = useMemo(() => {
    return localElements.filter(element => element.contexts.includes('INDEPENDENT') || checkToolAreaElementIsValidForContext(element, context, customContext));
  }, [localElements, context, customContext]);
  return /*#__PURE__*/jsx(ToolArea, {
    elements: sortedElements,
    localElements: filteredLocalElements,
    context: context,
    drawerAboveContent: drawerAboveContent
  });
}

var styles$J = {"layoutContainer":"layoutContainer_yMCSSKZoH4"};

const LayoutContainerContext = /*#__PURE__*/createContext(false);
function LayoutContainer({
  children
}) {
  return /*#__PURE__*/jsx(LayoutContainerContext.Provider, {
    value: true,
    children: /*#__PURE__*/jsx(BottomBarProvider, {
      children: /*#__PURE__*/jsxs("div", {
        className: styles$J.layoutContainer,
        children: [/*#__PURE__*/jsx("div", {
          children: children
        }), /*#__PURE__*/jsx(ToolAreaConnected, {})]
      })
    })
  });
}

var styles$I = {"titleArea":"titleArea_TE7U7yENC5","hasTabs":"hasTabs_EKyco1Se66","headline":"headline_BGPkVJXXms","titleAreaContainer":"titleAreaContainer_K6uWN7I56v","hasActionOutsideSidebar":"hasActionOutsideSidebar_H0gE2JYVYU","subline":"subline_O9aIbBOEk4","textSubline":"textSubline_ghvteypgj-","header":"header_QbIW-inAHK","breadcrumbs":"breadcrumbs_BebtnArY7O","tabs":"tabs_IyDV-vfpSy","subpageBackbutton":"subpageBackbutton_MjFida1NSV"};

function LoadingTitleArea() {
  return /*#__PURE__*/jsxs("div", {
    className: styles$I.titleArea,
    children: [/*#__PURE__*/jsx(Skeleton, {
      variant: "rect",
      width: 200,
      height: 33
    }), /*#__PURE__*/jsxs("div", {
      className: styles$I.subline,
      children: [/*#__PURE__*/jsx(Skeleton, {
        variant: "text",
        height: 33,
        display: "block",
        marginBottom: "0.2em"
      }), /*#__PURE__*/jsx(Skeleton, {
        variant: "text",
        height: 33,
        display: "block",
        marginBottom: "0.2em"
      }), /*#__PURE__*/jsx(Skeleton, {
        variant: "text",
        width: "43%",
        height: 33,
        display: "block",
        marginBottom: "0.2em"
      })]
    })]
  });
}

function SecondaryNavigation({
  secondaryNavigation
}) {
  const {
    push
  } = useHistory();
  const location = useLocation();
  const activeIndex = useMemo(() => {
    return secondaryNavigation?.items.findIndex(navItem => !!matchPath(location.pathname, {
      path: navItem.path ?? '',
      exact: false
    }));
  }, [location, secondaryNavigation?.items]);
  return /*#__PURE__*/jsx(Tab, {
    scrollable: true,
    activeIndex: activeIndex,
    children: secondaryNavigation?.items.map(tab => /*#__PURE__*/jsx(TabItem, {
      title: tab.label,
      onClick: () => {
        tab.onClick?.();
        if (tab.path) {
          push(tab.path);
        }
      },
      disabled: tab.disabled
    }, tab.label))
  });
}

const TitleArea = /*#__PURE__*/forwardRef(({
  backButton,
  title,
  subline,
  breadcrumbs,
  loading,
  action,
  shownInSidebar,
  shownBesidesSidebar = false,
  secondaryNavigation,
  className
}, ref) => {
  if (loading) {
    return /*#__PURE__*/jsx(LoadingTitleArea, {});
  }
  const hasAction = !!action;
  const hasSubline = !!subline;
  const hasTabs = !!secondaryNavigation;
  const heading = shownBesidesSidebar ? 'h3' : 'h2';
  return /*#__PURE__*/jsxs("div", {
    className: classNames(styles$I.titleAreaContainer, {
      [styles$I.hasActionOutsideSidebar]: hasAction && !shownInSidebar
    }, className),
    ref: ref,
    children: [/*#__PURE__*/jsxs("div", {
      className: classNames(styles$I.titleArea, {
        [styles$I.hasTabs]: hasTabs
      }),
      children: [breadcrumbs && /*#__PURE__*/jsx(Breadcrumbs, {
        breadcrumbs: breadcrumbs,
        wrapperComponent: Link,
        className: styles$I.breadcrumbs
      }), /*#__PURE__*/jsxs("div", {
        className: styles$I.header,
        children: [/*#__PURE__*/jsxs("div", {
          className: styles$I.headline,
          children: [backButton && /*#__PURE__*/jsx(IconButton, {
            icon: ArrowBackwards,
            className: styles$I.subpageBackbutton,
            onClick: backButton.onClick
          }), /*#__PURE__*/jsx(Typography, {
            variant: heading,
            margin: false,
            children: title
          })]
        }), hasAction && shownInSidebar && /*#__PURE__*/jsx(Button, {
          small: shownInSidebar,
          onClick: action?.handler,
          children: action?.title
        })]
      }), hasSubline && /*#__PURE__*/jsx("div", {
        className: classNames(styles$I.subline, {
          [styles$I.textSubline]: typeof subline === 'string'
        }),
        children: typeof subline === 'string' ? /*#__PURE__*/jsx(Typography, {
          margin: false,
          children: subline
        }) : subline
      }), hasTabs && /*#__PURE__*/jsx("div", {
        className: classNames(styles$I.tabs),
        children: /*#__PURE__*/jsx(SecondaryNavigation, {
          secondaryNavigation: secondaryNavigation
        })
      })]
    }), hasAction && !shownInSidebar && /*#__PURE__*/jsx(Button, {
      small: shownInSidebar,
      onClick: action?.handler,
      children: action?.title
    })]
  });
});

var styles$H = {"layoutContainer":"layoutContainer_GEctVhm4VI","innerLayout":"innerLayout_tn7k3WrTtX","smallSidebar":"smallSidebar_utCNqDy64M","withoutLayoutContainer":"withoutLayoutContainer_6JTltaTR-7","hasSidebar":"hasSidebar_qVmPP-e9pR","fullscreen":"fullscreen_fp4-u2c-EH","content":"content_EMp7eRVzC2","layoutHeader":"layoutHeader_747hATTCrB"};

const LayoutContext = /*#__PURE__*/React.createContext({});
const localToolAreaElements = [];
function Layout({
  title,
  subline,
  breadcrumbs,
  footer,
  sidebar,
  sidebarWidth = 'DEFAULT',
  children,
  loading = false,
  action,
  fullscreen = false,
  toolAreaElements = [],
  secondaryNavigation,
  backButton
}) {
  const hasSidebar = !!sidebar;
  const hasSecondaryNavigation = (secondaryNavigation?.items.length ?? 0) > 0;
  const [titleArea, {
    bottom: titleAreaHeight
  }] = useMeasure();
  const isInsideLayoutContainer = useContext(LayoutContainerContext);
  const classes = useMemo(() => classNames(styles$H.innerLayout, {
    [styles$H.hasSidebar]: hasSidebar
  }, {
    [styles$H.fullscreen]: fullscreen
  }, {
    [styles$H.withoutLayoutContainer]: !isInsideLayoutContainer
  }, {
    [styles$H.smallSidebar]: sidebarWidth === 'SMALL'
  }), [fullscreen, hasSidebar, isInsideLayoutContainer, sidebarWidth]);
  const contextValue = useMemo(() => ({
    title,
    subline,
    backButton,
    breadcrumbs,
    loading,
    action,
    hasSecondaryNavigation
  }), [title, subline, backButton, breadcrumbs, loading, action, hasSecondaryNavigation]);

  // This mapping from the old api can be removed, when everything is migrated to portal-home
  const mappedToolAreaElements = useMemo(() => {
    if (isInsideLayoutContainer) {
      return [];
    }
    return toolAreaElements?.map(element => {
      const component = () => /*#__PURE__*/jsx(Fragment, {
        children: element.component
      });
      return {
        type: 'COMPONENT',
        presentation: 'DRAWER',
        component: /*#__PURE__*/lazy(async () => ({
          default: component
        })),
        title: element.title,
        icon: element.icon,
        disabled: element.disabled,
        slug: element.slug,
        contexts: [RouteContext.MORTGAGE, RouteContext.CONSUMER_CREDIT, RouteContext.SUBMISSION]
      };
    }) ?? [];
  }, [isInsideLayoutContainer, toolAreaElements]);
  return /*#__PURE__*/jsx(LayoutContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/jsxs("div", {
      className: styles$H.layout,
      style: {
        '--layout-title-area-height': `${titleAreaHeight}px`
      },
      children: [hasSecondaryNavigation && /*#__PURE__*/jsx(TitleArea, {
        ref: titleArea,
        className: styles$H.layoutHeader,
        title: title,
        subline: subline,
        loading: loading,
        action: action,
        breadcrumbs: breadcrumbs,
        secondaryNavigation: secondaryNavigation,
        backButton: backButton
      }), /*#__PURE__*/jsxs("div", {
        className: classes,
        children: [sidebar, /*#__PURE__*/jsxs("div", {
          children: [/*#__PURE__*/jsxs("div", {
            className: styles$H.content,
            children: [!hasSidebar && !hasSecondaryNavigation && /*#__PURE__*/jsx(TitleArea, {
              title: title,
              subline: subline,
              loading: loading,
              action: action,
              breadcrumbs: breadcrumbs,
              backButton: backButton
            }), children, hasSidebar && /*#__PURE__*/jsx(BottomBar, {})]
          }), footer]
        }), !isInsideLayoutContainer && /*#__PURE__*/jsx(ToolArea, {
          elements: mappedToolAreaElements,
          localElements: localToolAreaElements
        })]
      }), !hasSidebar && /*#__PURE__*/jsx(BottomBar, {})]
    })
  });
}

var styles$G = {"logo":"logo_ldUUCaX28I"};

function ConnectedLogo({
  children
}) {
  const landingPageUrl = landingPageUrlPlugin.useExtension();
  return /*#__PURE__*/jsx("a", {
    href: landingPageUrl?.url ?? '/',
    className: styles$G.logo,
    children: children
  });
}

var styles$F = {"layout":"layout_Xk8g0J5oJf","twoColumn":"twoColumn_pmgZTOzoSY","inner":"inner_dXkosb3zAL","widgetContainer":"widgetContainer_iw1iRyz8K2","hasPageContent":"hasPageContent_oyutNbTgjS","headline":"headline_b-fVMo7-6a","links":"links_Pley9ThZwJ","captchaInfo":"captchaInfo_cHk2yJNjzI","contact":"contact_7a0m-PvHHX","phone":"phone_r7l7AT7OVB","aside":"aside_GFzTChdjMQ","mainContainer":"mainContainer_v4kJ5lvjE3","logoContainer":"logoContainer_1S4MCRTv5S","separator":"separator_jXYJJNJ2KF","contentContainer":"contentContainer_8IncsPFYHK"};

const CAPTCHA_LEGAL_NOTE = 'Dieses Formular ist durch CaptchaFox geschützt.';
function EntryLayout({
  title,
  pageContent,
  contact,
  children,
  additionalLinks = [],
  footer,
  logo,
  aside,
  legalNote = CAPTCHA_LEGAL_NOTE,
  className
}) {
  return /*#__PURE__*/jsxs("div", {
    className: classNames(styles$F.layout, {
      [styles$F.twoColumn]: !!aside
    }, className),
    children: [/*#__PURE__*/jsx("div", {
      className: styles$F.logoContainer,
      children: /*#__PURE__*/jsx(ConnectedLogo, {
        children: logo
      })
    }), /*#__PURE__*/jsxs("div", {
      className: classNames(styles$F.widgetContainer, {
        [styles$F.hasPageContent]: !!pageContent
      }),
      children: [/*#__PURE__*/jsxs("div", {
        className: classNames(styles$F.inner, {
          [styles$F.twoColumn]: !!aside
        }),
        children: [aside && /*#__PURE__*/jsx("aside", {
          className: styles$F.aside,
          children: aside
        }), /*#__PURE__*/jsxs("div", {
          className: styles$F.mainContainer,
          children: [title && /*#__PURE__*/jsx("div", {
            className: styles$F.headline,
            children: /*#__PURE__*/jsx(Typography, {
              margin: false,
              variant: "h2",
              children: title
            })
          }), children]
        })]
      }), additionalLinks && additionalLinks?.length > 0 && /*#__PURE__*/jsx("div", {
        className: styles$F.links,
        children: additionalLinks.map(link => /*#__PURE__*/jsx(Link$1, {
          inline: true,
          href: link.href,
          onClick: link.onClick,
          children: link.label
        }, link.label))
      }), contact && /*#__PURE__*/jsxs(Fragment, {
        children: [additionalLinks && additionalLinks?.length > 0 && /*#__PURE__*/jsx("hr", {
          className: styles$F.separator
        }), /*#__PURE__*/jsxs("div", {
          className: styles$F.contact,
          children: [/*#__PURE__*/jsx("span", {
            children: contact?.info
          }), /*#__PURE__*/jsxs("a", {
            href: `tel:${contact?.phoneNumber}`,
            className: styles$F.phone,
            children: [/*#__PURE__*/jsx(CallFilled, {}), " ", /*#__PURE__*/jsx("span", {
              children: contact?.phoneNumber
            })]
          })]
        })]
      }), /*#__PURE__*/jsx("div", {
        className: styles$F.captchaInfo,
        children: /*#__PURE__*/jsx(Typography, {
          margin: false,
          children: legalNote
        })
      })]
    }), pageContent, footer]
  });
}

var styles$E = {"sidebar":"sidebar_rdPWbqr-JD","WHITE":"WHITE_TdZWsp5AsO","TRANSPARENT":"TRANSPARENT_KyHj39AYSb","content":"content_vZb3PXIyWs","paddingWrap":"paddingWrap_iQpN-P-cxv","horizontalPaddingWrap":"horizontalPaddingWrap_2QeHtEj-od"};

function Sidebar({
  scrollBoxRef,
  variant = 'WHITE',
  children
}) {
  const {
    title,
    subline,
    backButton,
    breadcrumbs,
    loading,
    action,
    hasSecondaryNavigation
  } = useContext(LayoutContext);
  return /*#__PURE__*/jsxs("div", {
    className: classNames(styles$E.sidebar, styles$E[variant]),
    children: [(!!title || !!subline || !!action) && !hasSecondaryNavigation && /*#__PURE__*/jsx("div", {
      className: styles$E.paddingWrap,
      children: /*#__PURE__*/jsx(TitleArea, {
        title: title,
        subline: subline,
        backButton: backButton,
        loading: loading,
        action: action,
        breadcrumbs: breadcrumbs,
        shownInSidebar: true
      })
    }), /*#__PURE__*/jsx("div", {
      className: styles$E.content,
      ref: scrollBoxRef,
      children: /*#__PURE__*/jsx("div", {
        className: styles$E.horizontalPaddingWrap,
        children: children
      })
    })]
  });
}

var styles$D = {"section":"section_B4Z3vqBCIe","REGULAR":"REGULAR_O8CbiXIBBM","description":"description_yI9HzmjoFY","header":"header_byU-Wyh-rN"};

function Section({
  section,
  preferredHeading
}) {
  const classes = classNames(styles$D.section, styles$D[section.size ?? 'REGULAR']);
  const hasTitleOrDescription = !!section?.title || !!section?.description;
  return /*#__PURE__*/jsxs("section", {
    className: classes,
    children: [hasTitleOrDescription && /*#__PURE__*/jsxs("div", {
      className: styles$D.header,
      children: [/*#__PURE__*/jsx(Typography, {
        variant: preferredHeading,
        margin: false,
        children: section.title
      }), /*#__PURE__*/jsx(Typography, {
        className: styles$D.description,
        margin: false,
        children: section.description
      })]
    }), /*#__PURE__*/jsx("div", {
      className: styles$D.content,
      children: section.content
    })]
  });
}

var styles$C = {"sections":"sections_-xNxWYYf1I"};

function Sections({
  children
}) {
  return /*#__PURE__*/jsx("div", {
    className: styles$C.sections,
    children: children
  });
}

const Collapsible = /*#__PURE__*/forwardRef(({
  children,
  open,
  ...props
}, ref) => {
  return /*#__PURE__*/jsx(AnimateHeight, {
    height: open ? 'auto' : 0,
    children: /*#__PURE__*/jsx("div", {
      ...props,
      ref: ref,
      children: children
    })
  });
});

var styles$B = {"link":"link_t05rIPFJmV","button":"button_XmwB04e0mN"};

const Interactable = /*#__PURE__*/forwardRef((props, ref) => {
  if (props.href) {
    return /*#__PURE__*/jsx(Link, {
      ...props,
      className: classNames(styles$B.link, props.className),
      ref: ref,
      to: props.href,
      children: /*#__PURE__*/jsx(Fragment, {
        children: props.children
      })
    });
  }
  return /*#__PURE__*/jsx("button", {
    type: "button",
    ...props,
    className: classNames(styles$B.button, props.className),
    ref: ref,
    children: props.children
  });
});

var styles$A = {"listItem":"listItem_ljWE5iDDEn","collapsible":"collapsible_L2LxwH3U-g","item":"item_pkZSlx2wu5","selectable":"selectable_J03H-y9Vmx","active":"active_a8vkMlSlor","content":"content_mPC1Dus8kJ","icon":"icon_bwHkFlaDF5","iconUp":"iconUp_feZC-Fp6VJ","iconDown":"iconDown_8BBrGbCGuK","mark":"mark_LDny5Gowrw","text":"text_C6wfAo5CyV","contained":"contained_ZM9UBd-ECt"};

const ListItem = /*#__PURE__*/forwardRef(({
  children,
  open,
  sublist,
  arrow,
  marked,
  active,
  tag,
  variant = 'text',
  selectable = false,
  ...props
}, ref) => {
  const arrowIcon = {
    up: /*#__PURE__*/jsx(ArrowForwardIos, {
      className: classNames(styles$A.icon, styles$A.iconUp)
    }),
    down: /*#__PURE__*/jsx(ArrowForwardIos, {
      className: classNames(styles$A.icon, styles$A.iconDown)
    })
  }[arrow ?? 'down'];
  return /*#__PURE__*/jsxs("li", {
    className: styles$A.listItem,
    "aria-selected": active,
    children: [/*#__PURE__*/jsxs(Interactable, {
      ...props,
      ref: ref,
      className: classNames(styles$A.item, props.className, styles$A[variant], {
        [styles$A.active]: active,
        [styles$A.selectable]: selectable
      }),
      children: [/*#__PURE__*/jsx("span", {
        className: styles$A.content,
        children: children
      }), tag && /*#__PURE__*/jsx(MaybeWrapper, {
        condition: !!tag.description,
        wrapper: child => /*#__PURE__*/jsx(Tooltip, {
          content: tag.description,
          children: child
        }),
        children: /*#__PURE__*/jsx(InfoTag, {
          ...tag,
          size: "SMALL"
        })
      }), arrow && arrowIcon, marked && /*#__PURE__*/jsx("span", {
        className: styles$A.mark
      })]
    }), sublist && /*#__PURE__*/jsx(Collapsible, {
      className: styles$A.collapsible,
      open: open,
      children: sublist
    })]
  });
});

var styles$z = {"header":"header_BHDOUVHHHK"};

const ListHeader = /*#__PURE__*/forwardRef(({
  icon,
  label,
  ...props
}, ref) => {
  return /*#__PURE__*/jsxs("div", {
    ...props,
    ref: ref,
    className: classNames(styles$z.header, props.className),
    children: [icon, /*#__PURE__*/jsx(Typography, {
      variant: "h5",
      margin: false,
      children: label
    })]
  });
});

var styles$y = {"list":"list_yt0s5KjcKZ"};

const List = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, ref) => {
  return /*#__PURE__*/jsx("ul", {
    ...props,
    ref: ref,
    className: classNames(styles$y.list, props.className),
    children: children
  });
});

function LoadingPage() {
  return /*#__PURE__*/jsxs("div", {
    children: [/*#__PURE__*/jsx(Skeleton, {
      variant: "rect",
      height: "35vh",
      width: "100%"
    }), /*#__PURE__*/jsx(Skeleton, {
      variant: "rect",
      height: "30vh",
      width: "100%",
      marginTop: "0.5rem"
    })]
  });
}

/**
 * Check if one of the given papers is currently active in the viewport
 * @param papers
 * @param onActive
 * @param dependencyList
 */
const usePaperScrollDetection = ({
  papers,
  onActive
}, dependencyList) => {
  useLayoutEffect(() => {
    if (!papers || papers.length === 0) {
      return;
    }
    const paperRefs = papers.map(({
      slug
    }) => {
      return document.querySelector(`[data-paper-id="${slug}"]`);
    }).filter(el => !!el);

    // Use debounce to avoid jumping while scrolling
    const listener = debounce(() => {
      // Find the element which is the most visible one on the screen
      const bestVisibleElement = paperRefs.map(el => {
        const rect = el.getBoundingClientRect();
        const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
        const ratio = Math.max(0, visibleHeight / rect.height);
        return {
          el,
          ratio
        };
      }).sort((paperA, paperB) => paperB.ratio - paperA.ratio).find(paper => paper.ratio > 0);
      const targetId = (bestVisibleElement?.el).dataset?.paperId;
      if (targetId) {
        onActive(targetId);
      }
    }, 100);
    window.addEventListener('scroll', listener, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [papers, ...dependencyList]);
};

const isLazyComponent = component => {
  // element is a lazy component if it's not a valid ReactElement
  return ! /*#__PURE__*/isValidElement(component) && component !== undefined;
};

function OptionalLazyComponent({
  children
}) {
  if (isLazyComponent(children)) {
    const Component = children;
    return /*#__PURE__*/jsx(Component, {});
  }
  return children;
}

var styles$x = {"papersContainer":"papersContainer_yH72QCzQqn"};

function PapersContainer({
  children
}) {
  return /*#__PURE__*/jsx("div", {
    className: styles$x.papersContainer,
    children: children
  });
}

var styles$w = {"page":"page_3YFuztRNp3","description":"description_7NtzSqH1o8","pagePaper":"pagePaper_UO0A1pLdHN"};

function Page({
  title,
  description,
  action,
  shownBesidesSidebar = false,
  loading = false,
  children,
  sections,
  papers,
  id
}) {
  const hasTitleOrDescription = !!title || !!description;
  const {
    replace,
    location
  } = useHistory() ?? {};
  const {
    setIgnoreRouteBlockerOnLastPathLevel
  } = useContext(BottomBarContext);
  const pathWithoutPaper = location?.pathname.split('/').slice(0, -1).join('/');
  usePaperScrollDetection({
    papers,
    onActive: targetId => replace?.(`${pathWithoutPaper}/${targetId}`)
  }, [pathWithoutPaper]);
  useEffect(() => {
    if (papers && papers.length > 0) {
      setIgnoreRouteBlockerOnLastPathLevel(true);
    }
    return () => setIgnoreRouteBlockerOnLastPathLevel(false);
  }, [papers, setIgnoreRouteBlockerOnLastPathLevel]);
  return /*#__PURE__*/jsxs("div", {
    className: styles$w.page,
    id: id,
    children: [hasTitleOrDescription && /*#__PURE__*/jsx(TitleArea, {
      title: title,
      subline: description,
      action: action,
      shownBesidesSidebar: shownBesidesSidebar
    }), /*#__PURE__*/jsxs("div", {
      className: styles$w.content,
      children: [loading && /*#__PURE__*/jsx(LoadingPage, {}), !loading && /*#__PURE__*/jsxs(Suspense, {
        fallback: /*#__PURE__*/jsx(LoadingPage, {}),
        children: [sections && sections?.length > 0 &&
        /*#__PURE__*/
        // ATTENTION: sections are deprecated and should be removed soon!
        jsx(Sections, {
          children: sections.map((section, index) => /*#__PURE__*/jsx(Section, {
            section: section,
            preferredHeading: !shownBesidesSidebar && title ? 'h3' : 'h4'
          }, index))
        }), children, (papers?.length ?? 0) > 0 && /*#__PURE__*/jsx(PapersContainer, {
          children: papers.map(({
            slug,
            component
          }) => /*#__PURE__*/jsx("div", {
            "data-paper-id": slug,
            className: styles$w.pagePaper,
            children: /*#__PURE__*/jsx(Suspense, {
              fallback: /*#__PURE__*/jsx(Skeleton, {
                variant: "rect",
                height: "35vh",
                width: "100%"
              }),
              children: /*#__PURE__*/jsx(OptionalLazyComponent, {
                children: component
              })
            })
          }, slug))
        })]
      })]
    })]
  });
}

function LocalToolAreaProvider({
  children
}) {
  // Local hook handling
  const [localToolAreaElements, setLocalToolAreaElements] = useState([]);
  const registerToolAreaElement = useCallback(toolAreaElement => {
    setLocalToolAreaElements(prev => [...prev, toolAreaElement]);
  }, []);
  const unregisterToolAreaElement = useCallback(toolAreaElement => {
    setLocalToolAreaElements(prev => prev.filter(element => element !== toolAreaElement));
  }, []);

  // Plugin handling
  const [activatedToolAreaSymbols, setActivatedToolAreaSymbols] = useState([]);
  const pluginToolAreaElements = localToolAreaPlugin.useExtensions();
  const filteredPluginToolAreaElements = useMemo(() => pluginToolAreaElements.map(({
    token,
    getElement
  }) => {
    // Only use plugins which were activated before
    const activatedPlugin = activatedToolAreaSymbols.find(s => s.token === token);
    if (!activatedPlugin) {
      return undefined;
    }
    return getElement(activatedPlugin.data);
  }).filter(Boolean), [pluginToolAreaElements, activatedToolAreaSymbols]);
  const activateLocalToolAreaPlugin = useCallback(data => {
    setActivatedToolAreaSymbols(prev => [...prev, data]);
  }, []);
  const deactivateLocalToolAreaPlugin = useCallback(elementToken => {
    setActivatedToolAreaSymbols(prev => prev.filter(({
      token
    }) => token !== elementToken));
  }, []);
  const value = useMemo(() => ({
    // Merge local and plugin elements
    toolAreaElements: [...localToolAreaElements, ...filteredPluginToolAreaElements],
    registerToolAreaElement,
    unregisterToolAreaElement,
    activateLocalToolAreaPlugin,
    deactivateLocalToolAreaPlugin
  }), [localToolAreaElements, filteredPluginToolAreaElements, registerToolAreaElement, unregisterToolAreaElement, activateLocalToolAreaPlugin, deactivateLocalToolAreaPlugin]);
  return /*#__PURE__*/jsx(toolAreaContext.Provider, {
    value: value,
    children: children
  });
}

var styles$v = {"header":"header_0-aDbBD6rm","nested":"nested_JFPSOvsD6J","title":"title_03r7rCBvE9","description":"description_v1A41idfjP","side":"side_XYFzMhCNC3"};

function PaperHeaderLayout({
  title,
  description,
  sideContent,
  nested
}) {
  return /*#__PURE__*/jsxs("div", {
    className: classNames(styles$v.header, {
      [styles$v.nested]: nested
    }),
    children: [/*#__PURE__*/jsx(Typography, {
      variant: nested ? 'h5' : 'h4',
      margin: false,
      className: styles$v.title,
      children: title
    }), description !== undefined && /*#__PURE__*/jsx(Typography, {
      variant: "p",
      margin: false,
      className: styles$v.description,
      children: description
    }), sideContent && /*#__PURE__*/jsx("div", {
      className: styles$v.side,
      children: sideContent
    })]
  });
}

const isPaperSection = element => {
  return element.content !== undefined && element.title !== undefined;
};

var styles$u = {"paper":"paper_dvR1JDxkQt","notification":"notification_0gyRD7Y9bR","groupTitle":"groupTitle_HMEzaiELRc","group":"group_9O9LnPb5SG","section":"section_M0BTtkzlPB"};

function Paper({
  title,
  description,
  notification,
  sideContent,
  groups,
  children
}) {
  return /*#__PURE__*/jsxs("div", {
    className: styles$u.paper,
    children: [/*#__PURE__*/jsx(PaperHeaderLayout, {
      title: title,
      description: description,
      sideContent: sideContent
    }), notification && /*#__PURE__*/jsxs(Notification, {
      type: notification.type ?? 'error',
      className: styles$u.notification,
      children: [/*#__PURE__*/jsx(Typography, {
        variant: "strong",
        children: notification.title
      }), " ", notification.description]
    }), children, groups?.map((group, groupIndex) => /*#__PURE__*/jsxs("div", {
      className: styles$u.group,
      children: [/*#__PURE__*/jsx(GroupTitle, {
        title: group.title,
        className: styles$u.groupTitle,
        size: "SMALL",
        color: "LIGHT"
      }), group.sections?.map((section, sectionIndex) => /*#__PURE__*/jsx("div", {
        className: styles$u.section,
        children: isPaperSection(section) ? /*#__PURE__*/jsxs(Fragment, {
          children: [/*#__PURE__*/jsx(PaperHeaderLayout, {
            title: section.title,
            description: section.description,
            sideContent: section.sideContent,
            nested: true
          }), section.content]
        }) : section
      }, `${groupIndex}_${sectionIndex}`))]
    }, groupIndex))]
  });
}

var styles$t = {"cell":"cell_cGpGJ2f2FN"};

function DashboardGridCell({
  children,
  min,
  max,
  span,
  order,
  name,
  className
}) {
  return /*#__PURE__*/jsx("div", {
    "data-name": name,
    "data-min": min,
    "data-max": max,
    className: classNames([styles$t.cell, className]),
    style: {
      '--cell-span': span,
      '--cell-order': order
    },
    children: children
  });
}

var styles$s = {"swiper":"swiper_p9UVYrSVbJ","navigationArrow":"navigationArrow_wzpbXz07cE","disabled":"disabled_-m9JO7seOE","prev":"prev_IaMIZIA6mQ","next":"next_C5-TgimupB"};

if (process.env.NODE_ENV === 'test') {
  SwiperCore.use([Navigation, Pagination]);
} else {
  SwiperCore.use([Navigation, Pagination, A11y]);
}

/**
 * This component renders the given DashboardSwiperChild children as slides with specific span properties
 * @param children
 * @param className
 * @constructor
 */
function DashboardSwiper({
  children,
  className
}) {
  const [swiperInstance, setSwiperInstance] = useState(null);
  useLayoutEffect(() => {
    if (!swiperInstance || swiperInstance.destroyed) {
      return;
    }

    // Update instance after children change
    swiperInstance.update();
  }, [swiperInstance, children]);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("div", {
      className: classNames(styles$s.navigationArrow, styles$s.prev, `swiperPrev`),
      children: /*#__PURE__*/jsx(ArrowForwardIos, {})
    }), /*#__PURE__*/jsx(Swiper, {
      className: classNames(styles$s.swiper, className),
      slidesPerView: "auto",
      navigation: {
        disabledClass: styles$s.disabled,
        prevEl: '.swiperPrev',
        nextEl: '.swiperNext'
      },
      onSwiper: swiperInstance => {
        setSwiperInstance(swiperInstance);
      },
      children: children.filter(isNotNull).map((child, index) => {
        return /*#__PURE__*/jsx(SwiperSlide, {
          style: {
            '--cell-span': child.props.span
          },
          children: child
        }, index);
      })
    }), /*#__PURE__*/jsx("div", {
      className: classNames(styles$s.navigationArrow, styles$s.next, `swiperNext`),
      children: /*#__PURE__*/jsx(ArrowForwardIos, {})
    })]
  });
}

/**
 * Dummy component just to set the span prop to the child element
 * @param children
 * @constructor
 */
function DashboardSwiperChild({
  children
}) {
  return children;
}
function isNotNull(element) {
  return element !== null;
}

function getGroupMaxWidth(group) {
  const maxWidth = group.cells.reduce((sum, cell) => {
    return sum + (cell.maxWidth ?? 0);
  }, 0);
  return maxWidth > 0 ? maxWidth : undefined;
}

function getGroupMinWidth(group) {
  return group.cells.reduce((sum, cell) => {
    return sum + cell.minWidth;
  }, 0);
}

function isCellGroup(cellOrGroup) {
  return typeof cellOrGroup.cells !== 'undefined';
}

function isCellType(cellOrGroup) {
  return typeof cellOrGroup.minWidth !== 'undefined';
}

/**
 * Check if the element would exactly fit into the remaining space
 * @param gridColumns
 * @param usedSize
 * @param elementMinSpan
 * @param elementMaxSpan
 */
function elementUsesRemainingSpace(gridColumns, usedSize, elementMinSpan, elementMaxSpan) {
  const remainingSpace = gridColumns - usedSize;
  return !!elementMaxSpan && elementMaxSpan === remainingSpace || elementMinSpan === remainingSpace || !!elementMaxSpan && elementMaxSpan > remainingSpace && elementMinSpan < remainingSpace;
}

/**
 * Return the maximum span to fit into the remaining space
 * Return min span if it doesn't fit
 * @param gridColumns
 * @param usedSize
 * @param elementMinSpan
 * @param elementMaxSpan
 */
function getMaximumSpan(gridColumns, usedSize, elementMinSpan, elementMaxSpan) {
  const remainingSpace = gridColumns - usedSize;

  // always use the full width if possible
  if (elementMinSpan > gridColumns || elementMaxSpan && elementMaxSpan > gridColumns) {
    return gridColumns;
  }

  // use the maximum when the minimum is even higher than the remaining space
  if (elementMinSpan >= remainingSpace) {
    return elementMaxSpan ?? elementMinSpan;
  }

  // use the maximum if it's smaller than the remaining space
  if (elementMaxSpan && elementMaxSpan <= remainingSpace) {
    return elementMaxSpan;
  }

  // use the min span if there is no max span
  if (!elementMaxSpan) {
    return elementMinSpan;
  }

  // use the remaining space as default (it should be some number between min and max)
  return remainingSpace;
}

/**
 * Check if next element could fit when the current element is smaller
 * @param gridColumns
 * @param usedSize
 * @param elementMinSpan
 * @param elementMaxSpan
 * @param nextElementMinSpan
 * @return SpanSize - span size of the current element to fit the next one 0 = next doesn't fit
 */
function getSpanToFitNextElement(gridColumns, usedSize, elementMinSpan, elementMaxSpan, nextElementMinSpan) {
  if (!nextElementMinSpan || usedSize >= gridColumns || !elementMaxSpan) {
    return 0;
  }
  const maxSpansForCurrentElementWhenNextShouldFit = gridColumns - usedSize - nextElementMinSpan;
  if (maxSpansForCurrentElementWhenNextShouldFit < elementMinSpan) {
    return 0;
  }
  if (maxSpansForCurrentElementWhenNextShouldFit > elementMaxSpan) {
    return elementMaxSpan;
  }
  return maxSpansForCurrentElementWhenNextShouldFit;
}

function getOutput(gridColumns, elements, usedSize, spanSize, elementIndex = 0) {
  const element = elements[elementIndex];
  const nextElement = elements[elementIndex + 1];
  const elementMinSpan = Math.ceil(element.minWidth / spanSize) || 1;
  const elementMaxSpan = element.maxWidth ? Math.floor(element.maxWidth / spanSize) || 1 : undefined;
  const nextElementMinSpan = nextElement ? Math.ceil(nextElement.minWidth / spanSize) || 1 : undefined;

  // 1) fits exactly
  if (elementUsesRemainingSpace(gridColumns, usedSize, elementMinSpan, elementMaxSpan)) {
    return {
      name: element.name,
      span: gridColumns - usedSize,
      elementIndex
    };
  }

  // 2) should be smaller because of next element
  const sizeToFitNextElement = getSpanToFitNextElement(gridColumns, usedSize, elementMinSpan, elementMaxSpan, nextElementMinSpan);
  if (sizeToFitNextElement !== 0) {
    return {
      name: element.name,
      span: sizeToFitNextElement,
      elementIndex
    };
  }

  // 3) fits with the maximum remaining space
  return {
    name: element.name,
    span: getMaximumSpan(gridColumns, usedSize, elementMinSpan, elementMaxSpan),
    elementIndex
  };
}

/**
 * Define the cell spans depending on the given data and also re-order the cells to get the best grid
 * @param gridColumns
 * @param cells
 * @param currentWidth
 */
function reOrderAndReSize(gridColumns, cells, currentWidth) {
  const output = {};
  const spanSize = Math.round(currentWidth / gridColumns);
  const cellsCopy = [...cells];
  let usedSize = 0;
  let order = 0;
  while (cellsCopy.length > 0) {
    const {
      name,
      span,
      elementIndex
    } = getOutput(gridColumns, cellsCopy, usedSize, spanSize);
    output[name] = {
      order,
      span
    };
    usedSize += span;

    // next row
    if (usedSize > gridColumns) {
      usedSize = span;
    } else if (usedSize === gridColumns) {
      usedSize = 0;
    }
    order++;
    cellsCopy.splice(elementIndex, 1);
  }
  return output;
}

/**
 * Similar to reOrderAndReSize this function find the best span size for the given cells but without re-ordering
 * @param gridColumns
 * @param cells
 * @param currentWidth
 */
function swiperReSize(gridColumns, cells, currentWidth) {
  const output = {};
  if (cells.length === 0) {
    return {};
  }
  const maximumMinWidth = Math.max(...cells.map(c => c.minWidth));
  const minimumMaxWidth = Math.min(...cells.filter(c => c.maxWidth !== undefined).map(c => c.maxWidth));
  const widthToFitAll = currentWidth / cells.length;
  const spanToFitAll = Math.ceil(widthToFitAll / currentWidth * gridColumns);
  cells.forEach((cell, index) => {
    const minSpan = getMinSpan(cell, currentWidth, maximumMinWidth, gridColumns);
    const maxSpan = getMaxSpan(cell, currentWidth, minimumMaxWidth, gridColumns);
    let span;
    if (maxSpan < spanToFitAll) {
      span = maxSpan;
    } else if (minSpan <= spanToFitAll) {
      span = spanToFitAll;
    } else {
      span = minSpan;
    }
    if (span > gridColumns) {
      span = gridColumns;
    }
    output[cell.name] = {
      order: index,
      span
    };
  });
  return output;
}
function getMinSpan(cell, currentWidth, maximumMinWidth, gridColumns) {
  // find the ideal min width which should be the highest possible min width
  const idealMinWidth = maximumMinWidth >= cell.minWidth && maximumMinWidth <= (cell.maxWidth ?? Infinity) ? maximumMinWidth : cell.minWidth;
  return Math.ceil(idealMinWidth / currentWidth * gridColumns);
}
function getMaxSpan(cell, currentWidth, minimumMaxWidth, gridColumns) {
  // find the ideal max width which should be the lowest of the cell and the given minimum
  const idealMaxWidth = !cell.maxWidth || cell.maxWidth >= minimumMaxWidth ? minimumMaxWidth : cell.maxWidth;
  return Math.ceil(idealMaxWidth / currentWidth * gridColumns);
}

var styles$r = {"tabletBreak":"992px","mobileBreak":"576px","grid":"grid_m2sViUxLdO"};

const desktopGridColumns = 12;
const tabletGridColumns = 2;
const mobileGridColumns = 1;

function useGridColumns() {
  const isMobile = useMedia(`(max-width: ${styles$r.mobileBreak})`);
  const isTablet = useMedia(`(max-width: ${styles$r.tabletBreak})`);

  // use different column amount depending on the current screen
  const gridColumns = useMemo(() => {
    if (isMobile) {
      return mobileGridColumns;
    }
    if (isTablet) {
      return tabletGridColumns;
    }
    return desktopGridColumns;
  }, [isMobile, isTablet]);
  return gridColumns;
}

/**
 * Component which renders the given cells as children inside a grid with special width handling
 * @param props - DashboardGridProps
 * @constructor
 */
function DashboardGrid(props) {
  return /*#__PURE__*/jsx(InternalDashboardGrid, {
    ...props
  });
}
function InternalDashboardGrid({
  cellsAndGroups,
  className,
  cellClassName,
  swiperClassName,
  type
}) {
  const [ref, {
    width
  }] = useMeasure();
  const gridColumns = useGridColumns();
  const isSwiper = type === 'OVERFLOW_SWIPER';
  const filteredCellsAndGroups = useMemo(() => cellsAndGroups.filter(
  // Remove groups without cells
  cellOrGroup => !isCellGroup(cellOrGroup) || cellOrGroup.cells.length > 0), [cellsAndGroups]);
  const cellOrders = useMemo(() => {
    return (isSwiper ? swiperReSize : reOrderAndReSize).call({}, gridColumns, filteredCellsAndGroups.map((cellOrGroup, index) => {
      const name = `C${index}`;
      if (isCellType(cellOrGroup)) {
        const {
          content,
          component,
          name: _,
          ...cell
        } = cellOrGroup;
        return {
          name,
          ...cell
        };
      }
      return {
        name,
        minWidth: getGroupMinWidth(cellOrGroup),
        maxWidth: getGroupMaxWidth(cellOrGroup)
      };
    }), width);
  }, [filteredCellsAndGroups, width, gridColumns, isSwiper]);
  const cellContent = useMemo(() => {
    return filteredCellsAndGroups.map((cellOrGroup, index) => {
      const name = `C${index}`;
      const data = cellOrders[name];
      if (!data) {
        console.error(`Missing data for ${index}nth element`, cellOrGroup);
        return null;
      }
      if (isCellType(cellOrGroup)) {
        const Component = cellOrGroup.component;
        if (isSwiper) {
          return /*#__PURE__*/jsx(DashboardSwiperChild, {
            span: data.span,
            children: /*#__PURE__*/jsx(Suspense, {
              fallback: /*#__PURE__*/jsx(Skeleton, {
                minHeight: 120
              }),
              children: /*#__PURE__*/jsx(Fragment, {
                children: Component ? /*#__PURE__*/jsx(Component, {}) : cellOrGroup.content
              })
            })
          }, name);
        }
        return /*#__PURE__*/jsx(DashboardGridCell, {
          span: data.span,
          order: data.order,
          min: cellOrGroup.minWidth,
          max: cellOrGroup.maxWidth,
          name: name,
          className: cellClassName,
          children: /*#__PURE__*/jsx(Suspense, {
            fallback: /*#__PURE__*/jsx(Skeleton, {
              minHeight: 120
            }),
            children: /*#__PURE__*/jsx(Fragment, {
              children: Component ? /*#__PURE__*/jsx(Component, {}) : cellOrGroup.content
            })
          })
        }, name);
      }
      return /*#__PURE__*/jsx(DashboardGridCell, {
        span: data.span,
        order: data.order,
        min: getGroupMinWidth(cellOrGroup),
        max: getGroupMaxWidth(cellOrGroup),
        name: name,
        className: cellClassName,
        children: /*#__PURE__*/jsx(InternalDashboardGrid, {
          cellsAndGroups: cellOrGroup.cells,
          cellClassName: cellClassName,
          swiperClassName: swiperClassName,
          type: cellOrGroup.type
        })
      }, name);
    });
  }, [filteredCellsAndGroups, cellOrders, cellClassName, isSwiper, swiperClassName]);
  return /*#__PURE__*/jsx("div", {
    ref: ref,
    className: classNames([styles$r.grid, className]),
    "data-type": type,
    style: {
      '--dashboard-grid-cell-columns': gridColumns
    },
    children: isSwiper ? /*#__PURE__*/jsx(DashboardSwiper, {
      className: swiperClassName,
      children: cellContent
    }) : cellContent
  });
}

/**
 * The cells array includes the local cells
 * The rootCells array doesn't include the local cells
 */

const functionNotDefined = () => {
  console.error('DashboardGridContext not existing. ' + 'Maybe you have added a min-width to your cell which calls a context function (addCell / removeCell / addCellGroup / removeCellGroup). ' + 'Cells which interact with the context must have the type UnwrappedCell (= cells with no width)');
};
const dashboardGridContext = /*#__PURE__*/createContext({
  addCell: functionNotDefined,
  removeCell: functionNotDefined,
  addCellGroup: functionNotDefined,
  removeCellGroup: functionNotDefined
});

/**
 * This function merges the "standard" cells and groups with the local cells / groups and handle the positioning of the local cell elements depending on the forceIndex prop
 * @param standardCells
 * @param localCells
 */
function mergeCells(standardCells, localCells) {
  const orderedCells = [...standardCells];
  localCells.sort((cellA, cellB) => {
    // sort cells because otherwise the order of the elements would affect the position and would not fit with the forceIndex
    return (cellA.forceIndex ?? Infinity) - (cellB.forceIndex ?? -1);
  }).forEach(({
    forceIndex,
    ...other
  }) => {
    if (typeof forceIndex === 'undefined') {
      // elements without forceIndex will be added after all other cells
      orderedCells.push(other);
      return;
    }

    // insert the cell into the forceIndex position
    orderedCells.splice(forceIndex, 0, other);
  });
  return orderedCells;
}

/**
 * This hook splits the given mixed array into a standard-cells and unwrapped-cells array
 * @param allCells
 */
function splitCells(allCells) {
  const standard = [];
  const unwrapped = [];
  allCells.forEach(cell => {
    if (isDashboardElement(cell)) {
      standard.push(cell);
    } else {
      unwrapped.push(cell);
    }
  });
  return {
    standard,
    unwrapped
  };
}
function isDashboardElement(cell) {
  return typeof cell.minWidth !== 'undefined';
}

/**
 * DashboardGrid but with context around it to support local dashboard elements
 * @param cells
 * @param props
 * @constructor
 */
function DashboardGridConnected({
  cells,
  ...props
}) {
  const {
    standard: standardCells,
    unwrapped: unwrappedCells
  } = useMemo(() => splitCells(cells), [cells]);
  const [localCells, setLocalCells] = useState([]);
  const [localCellGroups, setLocalCellGroups] = useState([]);
  const addCell = useCallback(cell => {
    setLocalCells(prevCells => [...prevCells, cell]);
  }, []);
  const removeCell = useCallback(cell => {
    setLocalCells(prevCells => prevCells.filter(e => e !== cell));
  }, []);
  const addCellGroup = useCallback(group => {
    setLocalCellGroups(prevGroups => [...prevGroups, group]);
  }, []);
  const removeCellGroup = useCallback(group => {
    setLocalCellGroups(prevGroups => prevGroups.filter(e => e !== group));
  }, []);
  const cellsAndGroups = useMemo(() => mergeCells(mergeCells(standardCells, localCells), localCellGroups), [standardCells, localCells, localCellGroups]);
  const contextValue = useMemo(() => {
    return {
      addCell,
      removeCell,
      addCellGroup,
      removeCellGroup
    };
  }, [addCell, removeCell, addCellGroup, removeCellGroup]);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(DashboardGrid, {
      cellsAndGroups: cellsAndGroups,
      ...props
    }), /*#__PURE__*/jsx(dashboardGridContext.Provider, {
      value: contextValue,
      children: unwrappedCells.map(({
        content,
        component: Component
      }, index) => /*#__PURE__*/jsx(Suspense, {
        fallback: null,
        children: /*#__PURE__*/jsx(Fragment, {
          children: Component ? /*#__PURE__*/jsx(Component, {}) : content
        })
      }, index))
    })]
  });
}

var styles$q = {"sizeContainer":"sizeContainer_LxMufJ7HIj","sideNavigation":"sideNavigation_qjXTEXeFW3","collapsed":"collapsed_QRdEgiMSi7","collapseButton":"collapseButton_4kNf8Amyr9","collapseIcon":"collapseIcon_bxgZ-VdYYL","collapseButtonPositionContainer":"collapseButtonPositionContainer_-yAoeQhtP-","children":"children_nH6yIOIb6E"};

const sideNavigationValue = createStorageValue({
  id: Symbol('side-navigation-expanded'),
  namespace: portalNamespace,
  initialValue: false,
  type: 'local',
  schema: z.boolean()
});
function SideNavigation({
  children
}) {
  const [collapsed, setCollapsed] = sideNavigationValue.useValue();
  return /*#__PURE__*/jsx("div", {
    className: styles$q.sizeContainer,
    children: /*#__PURE__*/jsxs("div", {
      className: classNames(styles$q.sideNavigation, {
        [styles$q.collapsed]: collapsed
      }),
      children: [/*#__PURE__*/jsx("div", {
        className: styles$q.children,
        children: children({
          collapsed
        })
      }), /*#__PURE__*/jsx("div", {
        className: styles$q.collapseButtonPositionContainer,
        children: /*#__PURE__*/jsx(ResetButton, {
          className: styles$q.collapseButton,
          onClick: () => setCollapsed(!collapsed),
          "aria-hidden": true,
          children: /*#__PURE__*/jsx(ArrowForward, {
            className: styles$q.collapseIcon
          })
        })
      })]
    })
  });
}

var styles$p = {"navigationLayoutContainer":"navigationLayoutContainer_n-P0qxBli1","navigationLayout":"navigationLayout_d63nuLPs-J","header":"header_YdTuAlgUli","sideNavigation":"sideNavigation_nil8G0fAPw","mainContent":"mainContent_uuuRmPmHOM"};

function NavigationLayout({
  sideNavigation,
  header,
  children
}) {
  return /*#__PURE__*/jsx("div", {
    className: styles$p.navigationLayoutContainer,
    children: /*#__PURE__*/jsxs("div", {
      className: styles$p.navigationLayout,
      children: [/*#__PURE__*/jsx("div", {
        className: styles$p.header,
        children: header
      }), /*#__PURE__*/jsx("div", {
        className: styles$p.mainContent,
        children: children
      }), /*#__PURE__*/jsx("div", {
        className: styles$p.sideNavigation,
        children: sideNavigation
      })]
    })
  });
}

const useNavigationHasOverflow = () => {
  const [containerRef, containerSize] = useMeasure();
  const [contentRef, contentSize] = useMeasure();
  const isOverflowing = useMemo(() => {
    return contentSize.width > containerSize.width;
  }, [containerSize.width, contentSize.width]);
  return useMemo(() => {
    return {
      containerRef,
      contentRef,
      isOverflowing
    };
  }, [containerRef, contentRef, isOverflowing]);
};

var styles$o = {"backButton":"backButton_JVqD7x9-bm","icon":"icon_-RMvVJCltz"};

function BackButton({
  className,
  onClick
}) {
  return /*#__PURE__*/jsx("button", {
    type: "button",
    className: classNames(className, styles$o.backButton),
    onClick: () => onClick(),
    children: /*#__PURE__*/jsx(ArrowForward, {
      className: styles$o.icon
    })
  });
}

var styles$n = {"link":"link_5IbnIyxD0a","condensed":"condensed_tIDTfx6SqA","label":"label_jmIL1OC7le","active":"active_zv0xyOnAxI","background":"background_ub-JojBYLV","badge":"badge_8-eMGht1-3"};

function NavigationItem({
  active,
  activeIcon,
  icon,
  label,
  badge,
  condensed = false
}) {
  return /*#__PURE__*/jsxs("span", {
    className: classNames(styles$n.link, {
      [styles$n.active]: active,
      [styles$n.condensed]: condensed
    }),
    title: label,
    "data-title": label,
    children: [/*#__PURE__*/jsx(IconRenderer, {
      icon: active ? activeIcon ?? icon : icon
    }), /*#__PURE__*/jsxs("span", {
      className: classNames(styles$n.label, {
        [styles$n.condensed]: condensed
      }),
      children: [/*#__PURE__*/jsx("span", {
        children: label
      }), typeof badge !== 'undefined' && /*#__PURE__*/jsx("span", {
        className: styles$n.badge,
        children: badge
      })]
    })]
  });
}

var styles$m = {"button":"button_VzcfwSIY8Q"};

function ProfileActions({
  profileActions,
  onActionClick
}) {
  const sortedActions = useMemo(() => {
    return profileActions.sort((a, b) => (a.order ?? 50) - (b.order ?? 50));
  }, [profileActions]);
  return /*#__PURE__*/jsx("div", {
    children: sortedActions.map((action, index) => /*#__PURE__*/jsx(ResetButton, {
      onClick: () => {
        action.action();
        onActionClick?.();
      },
      className: styles$m.button,
      children: /*#__PURE__*/jsx(NavigationItem, {
        label: action.title,
        icon: action.icon
      })
    }, `${action.title}-${index}`))
  });
}

var styles$l = {"profileInfo":"profileInfo_vmpe1ORY-r","email":"email_7eKQJxKcl0"};

function ProfileInfo({
  avatar,
  identity
}) {
  return /*#__PURE__*/jsxs("div", {
    className: styles$l.profileInfo,
    children: [/*#__PURE__*/jsx("div", {
      children: avatar
    }), /*#__PURE__*/jsxs("div", {
      children: [/*#__PURE__*/jsx("strong", {
        children: identity.name
      }), /*#__PURE__*/jsx("p", {
        className: styles$l.email,
        children: identity.email
      })]
    })]
  });
}

var styles$k = {"verticalNavigation":"verticalNavigation_BWLs1gr7o-","condensed":"condensed_r-FTmWpeE6","label":"label_63r8ZYMrlJ","link":"link_6exVcNUh8Z","sectionTitle":"sectionTitle_33AQXYpN2q"};

function VerticalNavigation({
  links,
  condensed,
  onItemClick
}) {
  const {
    pathname
  } = useLocation();
  return /*#__PURE__*/jsx("div", {
    className: classNames(styles$k.verticalNavigation, {
      [styles$k.condensed]: condensed
    }),
    children: links.map((link, index) => {
      const active = !!matchPath(pathname, {
        path: link.to,
        exact: link.exact ?? false
      });
      return /*#__PURE__*/jsx(PlatformLink, {
        to: link.to,
        external: link.external,
        className: styles$k.link,
        onClick: () => onItemClick?.(),
        children: /*#__PURE__*/jsx(NavigationItem, {
          label: link.label,
          icon: link.icon,
          activeIcon: link.activeIcon,
          badge: link.badge,
          active: active,
          condensed: condensed
        })
      }, `${link.label}-${index}`);
    })
  });
}

var styles$j = {"hamburger":"hamburger_ZeMzZLmNvb","hamburgerOverlay":"hamburgerOverlay_jkSkw5WD0F","closeContainer":"closeContainer_PUW-MWNxhN","openProfileButton":"openProfileButton_WvBrc3dhze","profileSection":"profileSection_c5MTHNO4kx","titleContent":"titleContent_pU5ckoc6ip","chevron":"chevron_rNc3xh008l","open":"open_-ho3vVP4JP"};

function Hamburger({
  links,
  profileActions,
  avatar,
  titleContent,
  identity
}) {
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  return /*#__PURE__*/jsxs("div", {
    className: styles$j.hamburger,
    children: [/*#__PURE__*/jsx(IconButton, {
      icon: Menu,
      className: styles$j.hamburgerButton,
      toolbar: true,
      onClick: () => setOpen(true)
    }), open && /*#__PURE__*/jsxs("div", {
      className: styles$j.hamburgerOverlay,
      children: [/*#__PURE__*/jsx("div", {
        className: styles$j.titleContent,
        children: titleContent
      }), /*#__PURE__*/jsxs("div", {
        className: styles$j.profileSection,
        children: [/*#__PURE__*/jsxs(ClickableDiv, {
          onClick: () => setProfileOpen(prev => !prev),
          className: styles$j.openProfileButton,
          children: [/*#__PURE__*/jsx(ProfileInfo, {
            avatar: avatar,
            identity: identity
          }), (profileActions ?? 0).length > 0 && /*#__PURE__*/jsx(ChevronDown, {
            className: classNames(styles$j.chevron, {
              [styles$j.open]: profileOpen
            })
          })]
        }), (profileActions ?? 0).length > 0 && /*#__PURE__*/jsx(Collapsible, {
          open: profileOpen,
          children: /*#__PURE__*/jsx(ProfileActions, {
            profileActions: profileActions,
            onActionClick: () => setOpen(false)
          })
        })]
      }), /*#__PURE__*/jsx(VerticalNavigation, {
        links: links,
        onItemClick: () => setOpen(false)
      }), /*#__PURE__*/jsx("div", {
        className: styles$j.closeContainer,
        children: /*#__PURE__*/jsx(IconButton, {
          icon: Close,
          toolbar: true,
          onClick: () => setOpen(false)
        })
      })]
    })]
  });
}

var styles$i = {"icons":"icons_rzM6jIyx4o"};

function IconActions({
  icons
}) {
  return /*#__PURE__*/jsx("div", {
    className: styles$i.icons,
    children: icons.map(icon => /*#__PURE__*/jsx("div", {
      className: styles$i.icon,
      children: /*#__PURE__*/jsx(IconButton, {
        toolbar: true,
        onClick: icon.callback,
        children: /*#__PURE__*/jsx(IconRenderer, {
          icon: icon.icon
        })
      })
    }, icon.label))
  });
}

var styles$h = {"content":"content_rKoe9RsUCb","header":"header_dMzFsJVwsj"};

const PopoverContext = /*#__PURE__*/React.createContext(null);

const usePopoverContext = () => {
  const context = React.useContext(PopoverContext);
  if (context === null) {
    throw new Error('Popover components must be wrapped in <Popover />');
  }
  return context;
};

const PopoverContent = /*#__PURE__*/React.forwardRef(({
  style,
  ...props
}, propRef) => {
  const {
    context: floatingContext,
    ...context
  } = usePopoverContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);
  if (!floatingContext.open) {
    return null;
  }
  return /*#__PURE__*/jsx(FloatingPortal, {
    children: /*#__PURE__*/jsx(FloatingFocusManager, {
      context: floatingContext,
      children: /*#__PURE__*/jsx("div", {
        ref: ref,
        style: {
          ...context.floatingStyles,
          ...style
        },
        ...context.getFloatingProps({
          ...props,
          className: styles$h.content
        }),
        children: props.children
      })
    })
  });
});

const PopoverTrigger = /*#__PURE__*/React.forwardRef(({
  children,
  ...props
}, propRef) => {
  const context = usePopoverContext();
  const ref = useMergeRefs([context?.refs.setReference, propRef]);
  return /*#__PURE__*/jsx(ResetButton, {
    ref: ref,
    type: "button"
    // The user can style the trigger based on the state
    ,
    "data-state": context?.open ? 'open' : 'closed',
    ...context?.getReferenceProps(props),
    children: children
  });
});

function usePopover() {
  const [open, setOpen] = useState(false);
  const data = useFloating({
    placement: 'bottom-end',
    strategy: 'fixed',
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), shift({
      padding: 16
    })]
  });
  const {
    context
  } = data;
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const hover = useHover(context, {
    handleClose: safePolygon({
      requireIntent: false
    })
  });
  const interactions = useInteractions([dismiss, role, hover]);
  return useMemo(() => ({
    open,
    setOpen,
    ...interactions,
    ...data
  }), [open, setOpen, interactions, data]);
}

function ProfileTooltip({
  children,
  profileActions,
  avatar,
  identity
}) {
  const popover = usePopover();
  return /*#__PURE__*/jsx("div", {
    children: /*#__PURE__*/jsxs(PopoverContext.Provider, {
      value: popover,
      children: [/*#__PURE__*/jsx(PopoverTrigger, {
        children: children
      }), /*#__PURE__*/jsx(PopoverContent, {
        className: "Popover",
        children: /*#__PURE__*/jsxs("div", {
          className: styles$h.content,
          children: [/*#__PURE__*/jsx("div", {
            className: styles$h.header,
            children: /*#__PURE__*/jsx(ProfileInfo, {
              avatar: avatar,
              identity: identity
            })
          }), /*#__PURE__*/jsx(ProfileActions, {
            profileActions: profileActions,
            onActionClick: () => popover.setOpen(false)
          })]
        })
      })]
    })
  });
}

var styles$g = {"headerActions":"headerActions_USnIBHiNq7","avatar":"avatar_5uV3q4Qu6w"};

function HeaderActions({
  avatar,
  icons,
  links,
  profileActions,
  titleContent,
  identity
}) {
  return /*#__PURE__*/jsxs("div", {
    className: styles$g.headerActions,
    children: [/*#__PURE__*/jsx(IconActions, {
      icons: icons
    }), /*#__PURE__*/jsx("div", {
      className: styles$g.avatar,
      children: /*#__PURE__*/jsx(ProfileTooltip, {
        profileActions: profileActions,
        avatar: avatar,
        identity: identity,
        children: avatar
      })
    }), /*#__PURE__*/jsx(Hamburger, {
      links: links,
      avatar: avatar,
      profileActions: profileActions,
      titleContent: titleContent,
      identity: identity
    })]
  });
}

var styles$f = {"links":"links_8FQj2PLK9H","link":"link_FgrDpdilNA","active":"active_3LU38a2Nw2","visibleLink":"visibleLink_l8Lq-GRdTr","hiddenLink":"hiddenLink_WdrzI1kROc"};

function NavigationLinks({
  links,
  className
}) {
  const location = useLocation();
  return /*#__PURE__*/jsx("div", {
    className: classNames(styles$f.links, className),
    children: links.map(link => /*#__PURE__*/jsxs(PlatformLink, {
      to: link.to,
      external: link.external,
      className: classNames(styles$f.link, {
        [styles$f.active]: matchPath(location.pathname, {
          path: link.to,
          exact: link.exact ?? false
        })
      }),
      children: [/*#__PURE__*/jsx("span", {
        className: styles$f.visibleLink,
        children: link.label
      }), /*#__PURE__*/jsx("span", {
        className: styles$f.hiddenLink,
        children: link.label
      })]
    }, link.to))
  });
}

var styles$e = {"overflow":"overflow_HQUl5HLe5l","container":"container_erds4PsHA7","content":"content_mznIRm2JU-"};

function OverflowContainer({
  containerRef,
  contentRef,
  children
}) {
  return /*#__PURE__*/jsx("div", {
    className: styles$e.overflow,
    children: /*#__PURE__*/jsx("nav", {
      className: styles$e.container,
      ref: containerRef,
      children: /*#__PURE__*/jsx("div", {
        className: styles$e.content,
        ref: contentRef,
        children: children
      })
    })
  });
}

var styles$d = {"tileAndSubline":"tileAndSubline_wCAeu-K2p6","subline":"subline_wphuZ-sM-v","multipleSublineItem":"multipleSublineItem_kOuysFf-kL","tooltipList":"tooltipList_blrJUiNZY-","titleWrapper":"titleWrapper_LbO4GSy8vR","iconButton":"iconButton_-2XdLGRI6B","option":"option_apjMZxp01R","multipleSublineItemInfoTag":"multipleSublineItemInfoTag_qtfblz2L-o"};

function TitleAndSubline({
  title,
  subline,
  actions
}) {
  return /*#__PURE__*/jsxs("div", {
    className: styles$d.tileAndSubline,
    children: [/*#__PURE__*/jsxs("div", {
      className: styles$d.titleWrapper,
      children: [/*#__PURE__*/jsx(Typography, {
        variant: "h5",
        margin: false,
        children: title
      }), (actions?.length ?? 0) > 0 && /*#__PURE__*/jsx(ContextMenu, {
        label: "Aktionen",
        items: actions?.map(action => ({
          key: action.key,
          label: action.label,
          value: {
            callback: action.callback,
            icon: action.icon
          }
        })) ?? [],
        onSelect: a => a.value?.callback?.(),
        selectedItem: "",
        externalSearch: true // this has nothing to do with searching, but with the forced desktop ui
        ,
        optionWrapper: a => /*#__PURE__*/jsxs("div", {
          className: styles$d.option,
          children: [a.option.value?.icon && /*#__PURE__*/jsx(IconRenderer, {
            icon: a.option.value.icon
          }), a.option.label]
        }),
        children: getProps => /*#__PURE__*/jsx("button", {
          ...getProps(),
          type: "button",
          className: styles$d.iconButton,
          children: /*#__PURE__*/jsx(IconRenderer, {
            icon: MoreVert
          })
        })
      })]
    }), /*#__PURE__*/jsx("div", {
      className: styles$d.subline,
      children: subline.map((sublineItem, index) => /*#__PURE__*/jsxs(Fragment, {
        children: [typeof sublineItem === 'string' || sublineItem.length < 2 ? /*#__PURE__*/jsx("span", {
          children: typeof sublineItem === 'string' ? sublineItem : sublineItem?.[0]
        }, index) : /*#__PURE__*/jsxs("span", {
          className: styles$d.multipleSublineItem,
          children: [sublineItem?.[0], /*#__PURE__*/jsx(Tooltip, {
            placement: "bottom",
            zIndex: 1040,
            interactive: true,
            content: /*#__PURE__*/jsx("ul", {
              className: styles$d.tooltipList,
              children: sublineItem.map((item, index) => /*#__PURE__*/jsx("li", {
                children: item
              }, index))
            }),
            children: /*#__PURE__*/jsx(InfoTag, {
              label: `+ ${(sublineItem.length - 1).toString()}`,
              size: "SMALL",
              variant: "DEFAULT",
              className: styles$d.multipleSublineItemInfoTag
            })
          })]
        }), index !== subline.length - 1 && /*#__PURE__*/jsx("span", {
          children: "\u2022"
        })]
      }))
    })]
  });
}

var styles$c = {"header":"header_HEzWoaEiCH","overflowing":"overflowing_xPk6ZcGczE","hasBreadcrumbs":"hasBreadcrumbs_2HjFzaamOa","linksContainer":"linksContainer_m6GZexlPid","metaContainer":"metaContainer_iQLaIXgA9Z","breadcrumbs":"breadcrumbs_rUADnQ3Zbw","sizingContainer":"sizingContainer_T7AoLMbr9U","metaInformation":"metaInformation_FwkpYBERbT","links":"links_YY89Gr2k-9","accountContainer":"accountContainer_eMp0DANgmL"};

function ContextHeader({
  backBtnCallback,
  title,
  subline,
  breadcrumbs,
  links,
  icons,
  avatar,
  actions,
  resourceUnawareLinks,
  profileActions,
  identity
}) {
  const {
    containerRef,
    contentRef,
    isOverflowing
  } = useNavigationHasOverflow();
  const {
    containerRef: wrappingContainerRef,
    contentRef: wrappingContentRef,
    isOverflowing: isWrapping
  } = useNavigationHasOverflow();
  const combinedContentRef = useMergedRef(wrappingContentRef, contentRef);
  return /*#__PURE__*/jsxs("div", {
    className: classNames(styles$c.header, {
      [styles$c.hasBreadcrumbs]: !!breadcrumbs,
      [styles$c.overflowing]: isWrapping
    }),
    "data-navigation-links-overflowing": isOverflowing ? 'true' : undefined,
    children: [/*#__PURE__*/jsx("div", {
      className: styles$c.sizingContainer,
      ref: wrappingContainerRef
    }), /*#__PURE__*/jsxs("div", {
      className: styles$c.metaContainer,
      children: [/*#__PURE__*/jsx(BackButton, {
        onClick: backBtnCallback
      }), title && subline && /*#__PURE__*/jsx("div", {
        className: styles$c.metaInformation,
        children: /*#__PURE__*/jsx(TitleAndSubline, {
          title: title,
          subline: subline,
          actions: actions
        })
      })]
    }), (links && links.length > 0 || breadcrumbs) && /*#__PURE__*/jsx("div", {
      className: styles$c.linksContainer,
      children: /*#__PURE__*/jsxs(OverflowContainer, {
        containerRef: containerRef,
        contentRef: combinedContentRef,
        children: [links && /*#__PURE__*/jsx(NavigationLinks, {
          links: links,
          className: styles$c.links
        }), breadcrumbs && /*#__PURE__*/jsx("div", {
          className: styles$c.breadcrumbs,
          children: /*#__PURE__*/jsx(Breadcrumbs, {
            ...breadcrumbs
          })
        })]
      })
    }), /*#__PURE__*/jsx("div", {
      className: styles$c.accountContainer,
      children: /*#__PURE__*/jsx(HeaderActions, {
        icons: icons,
        avatar: avatar,
        links: resourceUnawareLinks,
        profileActions: profileActions,
        titleContent: title && subline ? /*#__PURE__*/jsx(TitleAndSubline, {
          title: title,
          subline: subline,
          actions: actions
        }) : undefined,
        identity: identity
      })
    })]
  });
}

var styles$b = {"header":"header_LbHTMtD8pV","left":"left_axB9Yl7h3Z","separator":"separator_UVlsVLGqyV","pageTitle":"pageTitle_cHE1gy08Jd"};

function Header({
  title,
  icons,
  avatar,
  links,
  profileActions,
  identity
}) {
  return /*#__PURE__*/jsxs("div", {
    className: styles$b.header,
    children: [/*#__PURE__*/jsxs("div", {
      className: styles$b.left,
      children: [/*#__PURE__*/jsx(Link, {
        to: "/",
        children: /*#__PURE__*/jsx(Logo, {
          height: 32,
          className: styles$b.logo
        })
      }), title && /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx(Separator, {
          variant: "VERTICAL",
          className: styles$b.separator
        }), ' ', /*#__PURE__*/jsx(Typography, {
          variant: "h3",
          margin: false,
          className: styles$b.pageTitle,
          children: title
        })]
      })]
    }), /*#__PURE__*/jsx(HeaderActions, {
      avatar: avatar,
      icons: icons,
      links: links,
      profileActions: profileActions,
      titleContent: /*#__PURE__*/jsx(Typography, {
        variant: "h3",
        margin: false,
        children: title
      }),
      identity: identity
    })]
  });
}

const parseGroups = items => {
  const groups = [];
  for (const sectionOrGroup of items) {
    if (sectionOrGroup.groups) {
      groups.push(...sectionOrGroup.groups);
    } else {
      groups.push(sectionOrGroup);
    }
  }
  return groups;
};

const useCurrentPageItem = ({
  items,
  activeItem
}) => {
  return useMemo(() => {
    if (!items || !activeItem) {
      return {
        item: undefined,
        parent: undefined
      };
    }
    const groups = parseGroups(items);

    // Find the group (parent) and the item for the given active item
    for (const group of groups) {
      const groupItems = group?.pages ?? group?.papers ?? [];

      // Skip item check if the parent slug doesn't match
      if (group.slug !== activeItem.parent?.slug) {
        continue;
      }
      for (const item of groupItems) {
        if (item.slug === activeItem.slug) {
          return {
            item,
            parent: group
          };
        }
      }
    }

    // Fallback if nothing found
    return {
      item: undefined,
      parent: undefined
    };
  }, [activeItem, items]);
};

var styles$a = {"loadingList":"loadingList_bs48E-4rh7"};

function LoadingListSection() {
  return /*#__PURE__*/jsx("div", {
    className: styles$a.loadingList,
    children: Array.from({
      length: 4
    }).map((item, index) => /*#__PURE__*/jsx(Skeleton, {
      variant: "rect",
      width: "100%",
      height: 80
    }, index))
  });
}

var styles$9 = {"listHeading":"listHeading_-BGuxZbYDE","description":"description_4ycLscU3Na","label":"label_tLNySzjYev"};

function ListGroup({
  group,
  activeItem,
  onSelection,
  expandedGroups
}) {
  const expanded = expandedGroups.includes(group.slug);
  const arrow = useMemo(() => {
    if ((group.pages?.length ?? 0) === 0 && (group.papers?.length ?? 0) === 0) {
      return undefined;
    }
    return expanded ? 'up' : 'down';
  }, [expanded, group.pages?.length, group.papers?.length]);
  const elements = group.pages ?? group.papers;
  const selfOrChildActive = useMemo(() => {
    const isActiveGroup = group.slug === activeItem?.slug && activeItem.type === 'GROUP';
    const subPageIsActive = elements.some(item => item.slug === activeItem?.slug && activeItem.parent?.slug === group.slug && (activeItem?.type === 'PAGE' || activeItem?.type === 'PAPER'));
    return isActiveGroup || subPageIsActive && !expanded;
  }, [activeItem, expanded, elements, group.slug]);
  return /*#__PURE__*/jsxs(ListItem, {
    variant: "contained",
    open: expanded,
    active: selfOrChildActive,
    onClick: () => onSelection({
      slug: group.slug,
      type: 'GROUP'
    }),
    selectable: group.selectable,
    arrow: arrow,
    sublist: elements?.length > 0 ? /*#__PURE__*/jsx(List, {
      children: elements.map(item => /*#__PURE__*/jsx(ListItem, {
        onClick: () => onSelection({
          slug: item.slug,
          type: group.papers ? 'PAPER' : 'PAGE',
          parent: {
            slug: group.slug,
            type: 'GROUP'
          }
        }),
        active: item.slug === activeItem?.slug && activeItem.parent?.slug === group.slug && activeItem?.type === 'PAGE',
        selectable: true,
        children: item.title
      }, item.slug))
    }) : undefined,
    children: [/*#__PURE__*/jsx(Typography, {
      className: styles$9.label,
      variant: "strong",
      margin: false,
      children: group.label
    }), /*#__PURE__*/jsx(Typography, {
      margin: false,
      className: styles$9.description,
      children: group.description
    })]
  });
}

var styles$8 = {"listGroups":"listGroups_VOQ7RU2slV"};

function ListGroups({
  groups,
  activeItem,
  onSelection,
  expandedGroups
}) {
  return /*#__PURE__*/jsx("div", {
    className: styles$8.listGroups,
    children: groups.map(group => /*#__PURE__*/jsx(ListGroup, {
      group: group,
      onSelection: onSelection,
      activeItem: activeItem,
      expandedGroups: expandedGroups
    }, group.slug))
  });
}

var styles$7 = {"navigationSections":"navigationSections_q8pjo0xpvE","sectionHeader":"sectionHeader_7MFpCEc-hE"};

function ListSection({
  items,
  onSelection,
  activeItem,
  expandedGroups,
  loading
}) {
  if (!items || loading) {
    return /*#__PURE__*/jsx(LoadingListSection, {});
  }
  return /*#__PURE__*/jsx("div", {
    className: styles$7.navigationSections,
    children: items.map(item => {
      if (item.groups) {
        return /*#__PURE__*/jsxs(Fragment$1, {
          children: [(!!item.icon || !!item.label) && /*#__PURE__*/jsx(ListHeader, {
            icon: item.icon,
            label: item.label,
            className: styles$7.sectionHeader
          }), /*#__PURE__*/jsx(ListGroups, {
            groups: item.groups,
            onSelection: onSelection,
            activeItem: activeItem,
            expandedGroups: expandedGroups
          })]
        }, item.slug);
      }
      return /*#__PURE__*/jsx(ListGroup, {
        group: item,
        onSelection: onSelection,
        activeItem: activeItem,
        expandedGroups: expandedGroups
      }, item.slug);
    })
  });
}

const useSidebarListLayout = ({
  items,
  activeItem,
  expandedGroups,
  handleActiveItemChange,
  loading,
  sidebarVariant
}) => {
  const sidebar = useMemo(() => {
    return /*#__PURE__*/jsx(Sidebar, {
      variant: sidebarVariant,
      children: /*#__PURE__*/jsx(ListSection, {
        items: items,
        activeItem: activeItem,
        onSelection: handleActiveItemChange,
        expandedGroups: expandedGroups ?? [],
        loading: loading
      })
    });
  }, [sidebarVariant, items, activeItem, handleActiveItemChange, expandedGroups, loading]);
  return {
    sidebar
  };
};

const useListLayout = ({
  items,
  activeItem,
  expandedGroups,
  handleActiveItemChange,
  loading,
  sidebarVariant
}) => {
  const {
    item: currentPage,
    parent: parentGroup
  } = useCurrentPageItem({
    items,
    activeItem
  });
  const {
    sidebar
  } = useSidebarListLayout({
    items,
    activeItem,
    expandedGroups,
    handleActiveItemChange,
    loading,
    sidebarVariant
  });
  const isPaper = !!parentGroup?.papers;
  const main = useMemo(() => {
    return /*#__PURE__*/jsx(Page, {
      title: isPaper ? parentGroup?.label : currentPage?.title,
      description: isPaper ? parentGroup?.description : currentPage?.description,
      action: isPaper ? undefined : currentPage?.action,
      sections: isPaper ? undefined : currentPage?.sections,
      papers: isPaper ? parentGroup?.papers : undefined,
      shownBesidesSidebar: true,
      loading: loading,
      children: !isPaper && !!currentPage?.component && /*#__PURE__*/jsx(OptionalLazyComponent, {
        children: currentPage.component
      })
    });
  }, [currentPage, loading, isPaper, parentGroup]);
  return {
    sidebar,
    main
  };
};

const useExpandedGroups = (items, active, singleItemOpen) => {
  const [expandedGroups, setExpandedGroups] = useState([]);
  const mergeExpandedGroups = useCallback(clickedGroup => previous => {
    const alreadyContainsGroup = previous.includes(clickedGroup.slug);
    if (!alreadyContainsGroup) {
      if (singleItemOpen) {
        return [clickedGroup.slug];
      }
      return [...previous, clickedGroup.slug];
    }
    const selfActive = clickedGroup.slug === active?.slug;
    if (!selfActive) {
      return previous.filter(item => item !== clickedGroup.slug);
    }
    return previous;
  }, [active, singleItemOpen]);
  const onActiveItemChange = useCallback(item => {
    if (!items) {
      throw new Error('Should not be called when no items are present');
    }
    const groups = parseGroups(items);
    const clickedGroup = groups.find(group => group.slug === item?.slug);
    if (item?.type === 'GROUP' && clickedGroup) {
      setExpandedGroups(mergeExpandedGroups(clickedGroup));
    }
  }, [items, mergeExpandedGroups]);
  return {
    expandedGroups,
    onActiveItemChange
  };
};

const useMemoryNavigation = items => {
  const [activeItem, setActiveItem] = useState();
  const {
    expandedGroups,
    onActiveItemChange
  } = useExpandedGroups(items, activeItem);
  const handleActiveItemChange = useCallback(newActiveItem => {
    if (newActiveItem) {
      if (newActiveItem.type === 'GROUP') {
        const groups = parseGroups(items);
        const groupIsSelectable = groups.find(group => group.slug === newActiveItem.slug)?.selectable;
        if (groupIsSelectable) {
          setActiveItem(newActiveItem);
          onActiveItemChange(newActiveItem);
        }
      } else {
        setActiveItem(newActiveItem);
        onActiveItemChange(newActiveItem);
      }
    }
  }, [items, onActiveItemChange]);
  return {
    activeItem,
    handleActiveItemChange,
    expandedGroups
  };
};

function joinPath(...args) {
  return `/${args.filter(Boolean).map(pathPart => pathPart.replace(/(^\/|\/$)/gu, '')).join('/')}`;
}

const useRouterNavigation = ({
  items,
  basePath = ''
}) => {
  const {
    push
  } = useHistory();
  const {
    pathname
  } = useLocation();
  const activeItem = useMemo(() => {
    const pathWithoutBasePath = pathname.startsWith(basePath) ? pathname.slice(basePath.length) : pathname.slice(1);
    const elements = pathWithoutBasePath.split('/').filter(Boolean);
    const type = pathWithoutBasePath.includes('/') ? 'PAGE' : 'GROUP';
    const parent = elements.length > 1 ? {
      slug: elements[elements.length - 2],
      type: 'GROUP'
    } : undefined;
    return {
      slug: elements[elements.length - 1],
      type,
      parent
    };
  }, [basePath, pathname]);
  const {
    expandedGroups,
    onActiveItemChange
  } = useExpandedGroups(items, activeItem);
  const handleActiveItemChange = useCallback(newActiveItem => {
    if (!items) {
      console.error('Expected items to be present');
      return;
    }
    const groups = parseGroups(items);
    if (newActiveItem) {
      const clickedGroup = groups.find(group => group.slug === newActiveItem.slug);
      const activeItemIsGroup = newActiveItem.type === 'GROUP' && clickedGroup;
      const activeItemIsPaper = newActiveItem.type === 'PAPER';
      const nothingSelected = !activeItem.slug;
      if (activeItemIsGroup) {
        if (clickedGroup?.selectable) {
          // If the group itself can be selected -> select the group
          const absolutePath = joinPath(basePath, clickedGroup?.slug);
          push(absolutePath);
        } else if (nothingSelected) {
          // If there is no current selection -> select the first child of this group
          const firstPageSlug = clickedGroup.pages?.[0]?.slug;
          const absolutePath = joinPath(basePath, clickedGroup?.slug, firstPageSlug ?? '');
          push(absolutePath);
        }
      } else {
        // Find group by page or paper
        const parentGroup = newActiveItem.parent ?? groups.find(group => (group.pages ?? group.papers).some(item => item.slug === newActiveItem.slug));
        push(joinPath(basePath, parentGroup?.slug ?? '', newActiveItem.slug));
        if (activeItemIsPaper) {
          // Scroll to element after navigating
          setTimeout(() => {
            const el = document.querySelector(`[data-paper-id="${newActiveItem.slug}"]`);
            if (el) {
              el.scrollIntoView({
                behavior: 'smooth'
              });
            }
          }, 0);
        }
      }
    }
    onActiveItemChange(newActiveItem);
  }, [activeItem, basePath, items, onActiveItemChange, push]);
  return {
    activeItem,
    handleActiveItemChange,
    expandedGroups
  };
};

function useNavigationItems(items) {
  return useMemo(() => items, [items]);
}

/**
 * Register a new local tool-area-element without using the plugins
 * @param toolAreaElement
 * @param deps
 */
const useLocalToolAreaElement = (toolAreaElement, deps) => {
  const {
    registerToolAreaElement,
    unregisterToolAreaElement
  } = useContext(toolAreaContext);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedToolAreaElement = useMemo(() => toolAreaElement, deps);
  useEffect(() => {
    registerToolAreaElement(memoizedToolAreaElement);
    return () => unregisterToolAreaElement(memoizedToolAreaElement);
  }, [memoizedToolAreaElement, registerToolAreaElement, unregisterToolAreaElement]);
};

/**
 * Activate / use a local tool-area-plugin which has been added by some other module / plugin project
 * @param elementToken
 * @param data
 */
function useLocalToolAreaPlugin(elementToken, data) {
  const {
    activateLocalToolAreaPlugin,
    deactivateLocalToolAreaPlugin
  } = useContext(toolAreaContext);
  useEffect(() => {
    activateLocalToolAreaPlugin({
      token: elementToken,
      data
    });
    return () => {
      deactivateLocalToolAreaPlugin(elementToken);
    };
  }, [activateLocalToolAreaPlugin, deactivateLocalToolAreaPlugin, elementToken, data]);
}

/**
 * This hook gives the possibility to add and remove local elements.
 * Works only inside the DashboardGridConnected component.
 */
function useDashboardGrid() {
  return useContext(dashboardGridContext);
}

/**
 * Registers a controller within bottom bar context
 * @param isDisabled - controls whether the bottom bar controls are interactable
 * @param isDirty - together with the isDirty flag of all registered controllers, controls
 * visibility of the bottom bar
 * @param submitHandler - only called if isDirty is true
 * @param discardHandler - only called if isDirty is true
 * @param dependencyList - dependency list when the controller updates (controller params
 * are used a fallback if not explicitly provided)
 */
const useBottomBar = ({
  isDisabled,
  isDirty,
  submitHandler,
  discardHandler
}, dependencyList = [isDisabled, isDirty, submitHandler, discardHandler]) => {
  const {
    addBottomBarController,
    removeBottomBarController
  } = useContext(BottomBarContext);
  useEffect(() => {
    // Register controller on mount
    const obj = {
      isDisabled,
      isDirty,
      submitHandler,
      discardHandler
    };
    addBottomBarController(obj);

    // Deregister on component demount
    return () => {
      removeBottomBarController(obj);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addBottomBarController, removeBottomBarController, ...dependencyList]);
};

var styles$6 = {"centerLayout":"centerLayout_erGKRU-OwF"};

/**
 * @deprecated Please use the `Layout` instead
 */
const CenterLayout = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, ref) => {
  return /*#__PURE__*/jsx("div", {
    ...props,
    ref: ref,
    className: classNames(styles$6.centerLayout, props.className),
    children: children
  });
});

var styles$5 = {"baseLayout":"baseLayout_VLPupgho2c"};

/**
 * @deprecated Please use the `Layout` instead
 */
const BaseLayout = /*#__PURE__*/forwardRef(({
  children,
  fullwidth,
  ...props
}, ref) => {
  if (fullwidth) {
    return /*#__PURE__*/jsx(Fragment, {
      children: children
    });
  }
  return /*#__PURE__*/jsx(CenterLayout, {
    ...props,
    ref: ref,
    className: classNames(styles$5.baseLayout, props.className),
    children: children
  });
});

var styles$4 = {"fixedLayout":"fixedLayout_sHZi8Dzt8l"};

/**
 * @deprecated Please use the `Layout` instead
 */
const FixedLayout = /*#__PURE__*/forwardRef(({
  children
}, ref) => {
  return /*#__PURE__*/jsx(CenterLayout, {
    ref: ref,
    className: styles$4.fixedLayout,
    children: children
  });
});

var styles$3 = {"layout":"layout_CWqUFazdme"};

/**
 * @deprecated Please use the `Layout` instead
 */
const ContentLayout = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, ref) => {
  return /*#__PURE__*/jsx("div", {
    ref: ref,
    ...props,
    className: styles$3.layout,
    children: children
  });
});

var styles$2 = {"layout":"layout_pTNVtrk0Bo","content":"content_8SSOWk-Bgy","split":"split_yKtypD5vmW"};

/**
 * @deprecated Please use the `Layout` instead
 */
const SidebarLayout = /*#__PURE__*/forwardRef(({
  children,
  menu,
  contentRef,
  ...props
}, ref) => {
  return /*#__PURE__*/jsxs("div", {
    ...props,
    ref: ref,
    className: classNames(styles$2.layout, props.className, {
      [styles$2.split]: !!menu
    }),
    children: [menu, /*#__PURE__*/jsx("div", {
      ref: contentRef,
      className: styles$2.content,
      children: children
    })]
  });
});

var styles$1 = {"layout":"layout_EttyyYy4dp"};

/**
 * @deprecated Please use the `Layout` instead
 */
const MainLayout = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, ref) => {
  return /*#__PURE__*/jsx("div", {
    ref: ref,
    ...props,
    className: styles$1.layout,
    children: children
  });
});

var styles = {"layout":"layout_I9HxhcB6bq"};

/**
 * @deprecated Please use the `Layout` instead
 */
const ToolbarLayout = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, ref) => {
  return /*#__PURE__*/jsx("div", {
    ref: ref,
    ...props,
    className: styles.layout,
    children: children
  });
});

export { BaseLayout, CenterLayout, ContentLayout, ContextHeader, DashboardGrid, DashboardGridConnected, EntryLayout, FixedLayout, Footer, FooterContext, Header, Layout, LayoutContainer, LayoutContainerContext, LayoutContext, List, ListHeader, ListItem, LocalToolAreaProvider, MainLayout, NavigationLayout, Page, Paper, RootLayout, Section, Sections, SideNavigation, Sidebar, SidebarLayout, ToolArea, ToolAreaConnected, ToolbarLayout, VerticalNavigation, useBottomBar, useDashboardGrid, useFallbackFooter, useListLayout, useLocalToolAreaElement, useLocalToolAreaPlugin, useMemoryNavigation, useNavigationItems, useRouterNavigation, useSidebarListLayout };
//# sourceMappingURL=index.js.map
